import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { AltYChatApi } from '../lib/useAltYChat';
import type { PinnedAgent } from '../lib/pinnedAgents';
import { EDITABLE_BUILTIN_PIN_IDS } from '../lib/pinnedAgents';
import type {
  AltYActivityEntry,
  AltYChatGroup,
  AltYChatMessage,
  AltYChatSummary,
  AltYCanvasContent,
  AltYEnvironmentTab,
  AltYImageAttachment,
} from '../types/global';
import { MarkdownText } from './MarkdownText';
import { ParticipantsPicker } from './ParticipantsPicker';
import { ParticipantsPopover } from './ParticipantsPopover';
import PrefixCostChip from './PrefixCostChip';
import { useVisualViewport } from '../lib/useVisualViewport';
import { useIsMobile } from '../lib/useIsMobile';
import { useScribe, SCRIBE_MODEL_LABELS, SCRIBE_LANGUAGE_LABELS, SCRIBE_LANGUAGE_AUTO, isGroqScribeModel, type ScribeModel } from '../lib/useScribe';
import {
  claudeFastModeTooltip,
  codexFastModeTooltip,
  cursorFastModeTooltip,
  cursorMaxModeTooltip,
  modelOptionPricingTooltip,
  pricingTipDataAttr,
} from '../lib/modelPricing';
import { formatAssistantCanvasChat } from '../lib/canvasBlocks';
import { getAltYSettings, updateAltYSettings, getBrokerStatus, restartBroker, waitForBrokerReady, getDeployedAppBuildId, searchAltYChats, getAltYTranscript } from '../lib/altYApi';
import { getChatSelection, setChatSelection } from '../lib/chatSelection';
import { AGENT_PRIMER_SEPARATOR } from '../lib/pinnedAgents';
import type { ConsensusModel } from '../lib/useAltYChat';
import { WorkspaceCanvas, type WorkspaceCanvasBadge } from './WorkspaceCanvas';

// The build id baked into this bundle at build time (see vite.config.ts). The
// panel polls the deployed bundle's id and offers a reload when it differs.
const RUNNING_BUILD_ID = __ALTY_BUILD_ID__;

// Claude's brand mark — an 8-pointed asterisk/star. Rendered inline so it
// tints with currentColor like the Font Awesome icons next to it.
export function ClaudeIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="currentColor" aria-hidden="true">
      <path d="M12 2 L13 9 L19.5 4.5 L15 11 L22 12 L15 13 L19.5 19.5 L13 15 L12 22 L11 15 L4.5 19.5 L9 13 L2 12 L9 11 L4.5 4.5 L11 9 Z" />
    </svg>
  );
}

type AltYPanelProps = {
  isOpen: boolean;
  // Mobile fold: full-bleed, no env tabs, no drag-resize handle, no
  // reserved-width reporting (there's no iframe to shrink alongside).
  mobile?: boolean;
  chat: AltYChatApi;
  // The OneSheet iframe — used by the resize-drag handler to suppress its
  // pointer events while dragging, otherwise the iframe swallows mousemove.
  appFrame: HTMLIFrameElement | null;
  tabs: AltYEnvironmentTab[];
  activeTabId: string;
  onSelectTab: (tab: AltYEnvironmentTab) => void;
  pinnedAgents: PinnedAgent[];
  // The picker's current selection — 'auto' or a real agent id. Drives the
  // dropdown trigger label/icon and which pin row in the list reads as active.
  selectedAgentId: string;
  // Which pin row to highlight as the "active" agent in the flat list below
  // the picker. Typically tracks selectedAgentId but can also reflect "the
  // pin whose creator chat is attached" (see App.tsx#visiblePinnedAgentId).
  selectedPinnedAgentId: string | null;
  onSelectPinnedAgent: (id: string | null) => void;
  onEditPinnedAgent: (id: string) => void;
  onDeletePinnedAgent: (id: string) => void;
  onStartNewChat: () => void;
  onStartSubchat?: () => Promise<number | null> | number | null | void;
  onBrokerRestartReady?: () => Promise<void> | void;
  // Cog-menu "Improve agent". Both are set together only when the current chat
  // is running a user-created agent: the action reflects on the chat against
  // that agent's diagram and rewrites the agent. Undefined/null otherwise, so
  // the button only renders when there's a concrete agent to improve.
  improveAgentLabel?: string | null;
  onImproveAgent?: () => void;
  // Mobile only: switch the shell from the chat to the online-app/dev iframe
  // view. Undefined on desktop (the iframes sit behind the panel there), so the
  // header toggle only renders when the host wires it up.
  onShowApp?: () => void;
  // Mobile only: when an agent-factory diagram has been closed for the current
  // chat, the header swaps the "view online app" globe for a "reopen diagram"
  // button that calls onReopenDiagram. canReopenDiagram gates that swap.
  canReopenDiagram?: boolean;
  onReopenDiagram?: () => void;
  // Idea Agent: when true the header shows an "ideas" toggle (in place of the
  // mobile "view online app" globe) that flips between the conversation and the
  // saved ideas list. `ideasOpen` reflects which is showing; `onToggleIdeas`
  // flips it. All three are undefined unless an Idea Agent is the active pick.
  canShowIdeas?: boolean;
  ideasOpen?: boolean;
  onToggleIdeas?: () => void;
  // Apartment finder: same as the ideas toggle but flips to the saved apartment
  // cards. `apartmentsOpen` reflects which is showing; `onToggleApartments`
  // flips it. Undefined unless the apartment finder is the active pick.
  canShowApartments?: boolean;
  apartmentsOpen?: boolean;
  onToggleApartments?: () => void;
  // Customized BMad Agent Flow: same as the apartments toggle but flips to the
  // workflow tabs. `bmadWorkflowOpen` reflects which is showing;
  // `onToggleBmadWorkflow` flips it. Undefined unless the BMad agent is active.
  canShowBmadWorkflow?: boolean;
  bmadWorkflowOpen?: boolean;
  onToggleBmadWorkflow?: () => void;
  // Goal Discovery: when the discovery agent is the active pick and it has
  // produced a brief, the header shows a "target" toggle that flips between
  // the conversation and the discovery brief.
  canShowDiscovery?: boolean;
  discoveryOpen?: boolean;
  onToggleDiscovery?: () => void;
  // Optional controlled state for the header settings (cog) popover. The mobile
  // app-view bar carries its own cog and needs to pop the same panel open after
  // flipping back to the chat view, so App owns the open flag there. Left
  // undefined on desktop, where the panel manages it internally.
  settingsOpen?: boolean;
  onSettingsOpenChange?: (open: boolean) => void;
  canvasOnly?: boolean;
  onCanvasOnlyChange?: (enabled: boolean) => void;
  canvasContent?: AltYCanvasContent | null;
  canvasBadge?: WorkspaceCanvasBadge;
  // Multi-select recipient list. An empty array routes to the agent (above);
  // a non-empty list bypasses the agent and broadcasts the message to each
  // selected user via sendHumanMessage.
  selectedUserIds?: string[];
  onSelectUserIds?: (userIds: string[]) => void;
};

const sizeKey = 'altY.panelSize.react';
const minWidth = 360;
const defaultWidth = 820;

function restoreWidth() {
  try {
    const parsed = JSON.parse(localStorage.getItem(sizeKey) || '{}');
    return Number(parsed.width) || defaultWidth;
  } catch {
    return defaultWidth;
  }
}

function clampWidth(width: number) {
  return Math.max(minWidth, Math.min(width, window.innerWidth - 20));
}

function formatAgo(value: string) {
  const time = new Date(value).getTime();
  if (!Number.isFinite(time)) return '';
  const seconds = Math.max(1, Math.round((Date.now() - time) / 1000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h`;
  return `${Math.round(seconds / 86400)}d`;
}

function stripAgentPrimer(text: string): string {
  const separatorIndex = text.indexOf(AGENT_PRIMER_SEPARATOR);
  return separatorIndex >= 0 ? text.slice(separatorIndex + AGENT_PRIMER_SEPARATOR.length) : text;
}

function firstUserQuestion(events: unknown[]): string {
  for (const event of events) {
    if (!event || typeof event !== 'object') continue;
    const record = event as Record<string, unknown>;
    if (record.type !== '_user') continue;
    const rawText = typeof record.text === 'string' ? record.text : '';
    const text = stripAgentPrimer(rawText).trim();
    const imageCount = Array.isArray(record.images) ? record.images.length : 0;
    if (text && imageCount) return `${text}\n\n(${imageCount} attached image${imageCount === 1 ? '' : 's'})`;
    if (text) return text;
    if (imageCount) return `${imageCount} attached image${imageCount === 1 ? '' : 's'}`;
  }
  return '';
}

const maxImageEdge = 1400;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });
}

const imageExtRe = /\.(png|jpe?g|gif|webp|avif|heic|heif|bmp|svg)$/i;

function isImageFile(file: File | null | undefined): file is File {
  if (!file) return false;
  if (file.type && file.type.startsWith('image/')) return true;
  // Some browsers (and some clipboard sources) hand us a File with an empty
  // type. Fall back to the filename extension so we don't silently drop it.
  return imageExtRe.test(file.name || '');
}

async function fileToBase64(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('file read failed'));
    reader.readAsDataURL(file);
  });
  return dataUrl.slice(dataUrl.indexOf(',') + 1);
}

// Decode a pasted/dropped image file, downscale it so the longest edge is at
// most maxImageEdge (keeps the WS frame + transcript small), and return it as a
// base64 attachment the broker can hand to claude's Read tool. If the image
// can't be decoded for any reason, fall back to sending the raw file bytes so
// the user's paste is never silently dropped.
async function fileToImageAttachment(file: File): Promise<AltYImageAttachment> {
  const inferredMedia = file.type && file.type.startsWith('image/')
    ? file.type
    : (imageExtRe.test(file.name) ? `image/${file.name.split('.').pop()!.toLowerCase().replace('jpg', 'jpeg')}` : 'image/png');
  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('file read failed'));
      reader.readAsDataURL(file);
    });
    const img = await loadImage(dataUrl);
    let width = img.naturalWidth || img.width;
    let height = img.naturalHeight || img.height;
    const longest = Math.max(width, height);
    if (longest > maxImageEdge) {
      const scale = maxImageEdge / longest;
      width = Math.max(1, Math.round(width * scale));
      height = Math.max(1, Math.round(height * scale));
    }
    const mediaType = inferredMedia === 'image/jpeg' ? 'image/jpeg' : 'image/png';
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas unavailable');
    ctx.drawImage(img, 0, 0, width, height);
    const encoded = canvas.toDataURL(mediaType, 0.92);
    const dataBase64 = encoded.slice(encoded.indexOf(',') + 1);
    const ext = mediaType === 'image/jpeg' ? 'jpg' : 'png';
    return { name: file.name || `pasted-${Date.now()}.${ext}`, mediaType, dataBase64 };
  } catch (error) {
    // Canvas decode failed (huge image, HEIC, security error, …). Send the
    // raw bytes — claude's Read tool can still open the file even if we
    // couldn't render a preview thumbnail.
    console.warn('Alt-Y: image decode failed, sending raw bytes', error);
    const dataBase64 = await fileToBase64(file);
    return { name: file.name || `pasted-${Date.now()}`, mediaType: inferredMedia, dataBase64 };
  }
}

// Turn any picked file into an attachment. Images go through the downscaling
// path above; everything else (PDF, CSV, text, …) is sent as raw bytes with its
// real type + name preserved, so the broker saves it under the right extension
// and claude's Read tool can open it.
async function fileToAttachment(file: File): Promise<AltYImageAttachment> {
  // Plain boolean test (not the `file is File` guard, which would narrow the
  // else branch to `never` since `file` is already typed `File`).
  const looksLikeImage = (!!file.type && file.type.startsWith('image/')) || imageExtRe.test(file.name || '');
  if (looksLikeImage) return fileToImageAttachment(file);
  const dataBase64 = await fileToBase64(file);
  return {
    name: file.name || `file-${Date.now()}`,
    mediaType: file.type || 'application/octet-stream',
    dataBase64,
  };
}

function imagesFromDataTransfer(data: DataTransfer | null): File[] {
  const found: File[] = [];
  if (!data) return found;
  // Dedup by content properties, not File identity: `item.getAsFile()` returns
  // a fresh File reference every call, and Chromium on paste surfaces the same
  // image in BOTH `data.items` AND `data.files` — a reference-keyed Set never
  // catches the duplicate, so the same screenshot ended up sent twice.
  const seen = new Set<string>();
  const push = (file: File | null) => {
    if (!isImageFile(file)) return;
    const key = `${file.name}|${file.size}|${file.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    found.push(file);
  };
  for (let i = 0; i < data.items.length; i += 1) {
    const item = data.items[i];
    if (item.kind !== 'file') continue;
    push(item.getAsFile());
  }
  // Some browsers populate `files` but leave `items` empty (notably on drop,
  // and on some clipboard sources). Pull from there as a fallback.
  for (let i = 0; i < data.files.length; i += 1) {
    push(data.files[i]);
  }
  return found;
}

// Clipboard screenshots can arrive as multiple File entries with different
// metadata, so de-dupe the converted payloads before showing previews.
function appendUniqueAttachments(current: AltYImageAttachment[], incoming: AltYImageAttachment[]) {
  const seen = new Set(current.map((attachment) => `${attachment.mediaType}|${attachment.dataBase64}`));
  const next = [...current];
  for (const attachment of incoming) {
    const key = `${attachment.mediaType}|${attachment.dataBase64}`;
    if (seen.has(key)) continue;
    seen.add(key);
    next.push(attachment);
  }
  return next;
}

// Mode → small icon next to each chat-row title so the unified list reads at a
// glance. Mirrors the icons used for the matching built-in pinned agents in
// pinnedAgents.ts (Interact / Agent factory / Alty Dev).
const MODE_BADGE_ICON: Record<string, string> = {
  'interact': 'fa-comments',
  'chat': 'fa-comment-dots',
  'agent-factory': 'fa-sitemap',
  'alty-dev': 'fa-laptop-code',
  'online-dev-mvp': 'fa-globe',
  'bmad': 'fa-people-carry',
  'scope-tasks': 'fa-clipboard-list',
};

const MODE_BADGE_LABEL: Record<string, string> = {
  'interact': 'Interact',
  'chat': 'Chat',
  'agent-factory': 'Agent factory',
  'alty-dev': 'Alty Dev',
  'online-dev-mvp': 'Online Dev MVP',
  'bmad': 'BMAD',
  'scope-tasks': 'Scope tasks',
};

// The composer model picker combines model + thinking into one dropdown.
// Gemini is high-only and Cerebras has none, so they list no levels. Fast mode
// for claude/codex lives in the cog menu; Cursor Composer gets a header bolt
// toggle beside the model picker. The chosen model maps to the broker's
// `effort` routing token; thinking + fast ride on `codexEffort`/`fast`.
type AltYModel = 'claude' | 'gemini' | 'codex' | 'cerebras' | 'cursor' | 'grok';

const MODEL_LABELS: Record<AltYModel, string> = {
  claude: 'Claude',
  gemini: 'Gemini',
  codex: 'Codex',
  cerebras: 'Cerebras',
  cursor: 'Cursor Composer 2.5',
  grok: 'Grok Build',
};

const THINKING_LABELS: Record<string, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  xhigh: 'X-High',
  max: 'Max',
};

// Per-model thinking levels. Claude goes low→max; Codex low→xhigh (no "max").
// Cursor/Grok bake the highest level in at the broker (no user-facing dropdown).
const THINKING_BY_MODEL: Record<AltYModel, string[]> = {
  claude: ['low', 'medium', 'high', 'xhigh', 'max'],
  codex: ['low', 'medium', 'high', 'xhigh'],
  gemini: [],
  cerebras: [],
  cursor: [],
  grok: [],
};

const FAST_MODELS = new Set<AltYModel>(['claude', 'codex', 'cursor']);
// Max Mode is a Cursor-only knob (Composer 2.5 Max Mode). Independent of Fast,
// so users can pick fast/max/fast+max/neither.
const MAX_MODELS = new Set<AltYModel>(['cursor']);

function isAltYModel(value: string | null): value is AltYModel {
  return value === 'claude' || value === 'gemini' || value === 'codex' || value === 'cerebras'
    || value === 'cursor' || value === 'grok';
}

// The model + thinking level used to be one overloaded select stored as
// `altY.effort`. Read the new per-field keys, falling back to a one-time
// migration of that legacy value so an existing selection isn't lost.
function readInitialModel(): AltYModel {
  const stored = localStorage.getItem('altY.model');
  if (isAltYModel(stored)) return stored;
  const legacy = localStorage.getItem('altY.effort');
  if (legacy === 'gemini-3.5') return 'gemini';
  if (legacy === 'cerebras') return 'cerebras';
  if (legacy === 'codex') return 'codex';
  return 'claude';
}

function readInitialThinking(): string {
  const stored = localStorage.getItem('altY.thinking');
  if (stored && THINKING_LABELS[stored]) return stored;
  const legacy = localStorage.getItem('altY.effort');
  if (legacy && THINKING_LABELS[legacy]) return legacy;
  return 'high';
}

// Every "model[:thinking]" option string the picker offers. The order here is
// the order the dropdown renders them in (and the consensus list will follow).
function allModelOptionValues(): string[] {
  const out: string[] = [];
  for (const id of Object.keys(MODEL_LABELS) as AltYModel[]) {
    const levels = THINKING_BY_MODEL[id];
    if (levels.length === 0) out.push(id);
    else for (const level of levels) out.push(`${id}:${level}`);
  }
  return out;
}

function labelForModelOption(value: string): string {
  const [m, t] = value.split(':');
  if (!isAltYModel(m)) return value;
  if (t && THINKING_LABELS[t]) return `${MODEL_LABELS[m]} · ${THINKING_LABELS[t]}`;
  return MODEL_LABELS[m];
}

function parseModelOption(value: string): { model: AltYModel; thinking?: string } | null {
  const [m, t] = value.split(':');
  if (!isAltYModel(m)) return null;
  if (t && THINKING_LABELS[t]) return { model: m, thinking: t };
  return { model: m };
}

// Additional models beyond the primary one. Empty = single-model behavior;
// >=1 = consensus turn (broker fans the prompt out into one subchat per model).
function readInitialExtraModels(): string[] {
  try {
    const raw = localStorage.getItem('altY.extraModels.v1');
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const valid = new Set(allModelOptionValues());
    return parsed.filter((v: unknown): v is string => typeof v === 'string' && valid.has(v));
  } catch {
    return [];
  }
}

// navigator.clipboard is unavailable on some plain-http/dev surfaces. Keep the
// cog-menu ID copy usable there too.
async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    if (!document.execCommand('copy')) throw new Error('copy command rejected');
  } finally {
    document.body.removeChild(textarea);
  }
}

// Stable callback shapes (take runId, not bound closures) so React.memo
// actually skips re-renders when an unrelated chat-state tick fires. Without
// this, inline-arrow onSelect/onStop/onDelete props broke shallow equality on
// every parent render and every row in the sidebar re-rendered.
type ChatRowProps = {
  chat: AltYChatSummary;
  active: boolean;
  expanded: boolean;
  parentTitle?: string | null;
  onSelect: (runId: number) => void;
  onDelete: (runId: number, title: string) => void;
  onStop: (runId: number) => void;
  // Drag handlers — set on the expanded sidebar so a chat can be dragged into
  // a folder or back to the ungrouped list. Unset on the slim icon-rail.
  onDragStart?: (runId: number, event: React.DragEvent) => void;
  onDragEnd?: () => void;
  dragging?: boolean;
};

type ChatDetailsState = {
  chat: AltYChatSummary;
  originalQuestion: string;
  loading: boolean;
  error: string;
};

function ChatRowImpl({
  chat,
  active,
  expanded,
  parentTitle,
  onSelect,
  onDelete,
  onStop,
  onDragStart,
  onDragEnd,
  dragging,
}: ChatRowProps) {
  const running = chat.status === 0 || chat.status === 1;
  const parentRunId = typeof chat.parentRunId === 'number' ? chat.parentRunId : null;
  const isSubchat = !!parentRunId;
  const modeIcon = MODE_BADGE_ICON[chat.mode] || 'fa-comments';
  const modeLabel = MODE_BADGE_LABEL[chat.mode] || chat.mode;
  const explicitTitle = (chat.title || '').trim();
  const title = explicitTitle || (isSubchat ? '' : `Chat ${chat.runId}`);
  const confirmTitle = title || 'Untitled subchat';
  const depth = Math.min(Math.max(chat.depth ?? (isSubchat ? 1 : 0), 0), 4);
  // Hover tooltip: the ~20-word "what was done" summary when we have one (the
  // row label is the 5-word version), else fall back to the title. Mode + age
  // ride along so the tooltip still tells you which agent + how recent.
  const familyHint = isSubchat
    ? ` · Subchat of ${parentTitle || `Chat ${parentRunId}`}`
    : '';
  const overallSummary = chat.overallSummary?.trim() || chat.subtitle?.trim() || '';
  const lastTurnSummary = chat.lastTurnSummary?.trim() || '';
  const tooltipSummary = [
    lastTurnSummary ? `Last turn: ${lastTurnSummary}` : '',
    overallSummary && overallSummary !== lastTurnSummary ? `Overall: ${overallSummary}` : '',
  ].filter(Boolean).join(' · ') || title || 'Subchat';
  const sharedHint = chat.shared
    ? ` · Shared by ${chat.ownerLabel || 'another user'}`
    : '';
  const tooltip = `${tooltipSummary} · ${modeLabel} · ${formatAgo(chat.lastActivity)}${familyHint}${sharedHint}`;
  const handleSelect = () => onSelect(chat.runId);
  const handleStop = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onStop(chat.runId);
  };
  const handleDelete = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDelete(chat.runId, confirmTitle);
  };
  return (
    <button
      type="button"
      className={[
        'alty-chat-row',
        active ? 'active' : '',
        isSubchat ? 'subchat' : '',
        dragging ? 'dragging' : '',
      ].filter(Boolean).join(' ')}
      style={{ '--alty-chat-depth': depth } as React.CSSProperties}
      title={tooltip}
      onClick={handleSelect}
      draggable={!!onDragStart}
      onDragStart={onDragStart ? (event) => onDragStart(chat.runId, event) : undefined}
      onDragEnd={onDragEnd}
    >
      <span className={running ? 'alty-chat-dot running' : 'alty-chat-dot'} />
      {expanded && isSubchat ? (
        <span className="alty-subchat-marker" aria-hidden="true">
          <i className="fas fa-code-branch" />
        </span>
      ) : null}
      {expanded ? (
        <span className="alty-chat-mode-badge" aria-label={modeLabel}>
          <i className={`fas ${modeIcon}`} aria-hidden="true" />
        </span>
      ) : null}
      {expanded && chat.shared ? (
        <span
          className="alty-chat-shared-badge"
          title={`Shared by ${chat.ownerLabel || 'another user'}`}
          aria-label={`Shared by ${chat.ownerLabel || 'another user'}`}
        >
          <i className="fas fa-share-alt" aria-hidden="true" />
        </span>
      ) : null}
      {expanded && title ? (
        <span className="alty-chat-title">
          {title}
        </span>
      ) : null}
      {expanded && running ? (
        <span
          role="button"
          tabIndex={0}
          className="alty-chat-stop"
          title="Stop chat"
          onClick={handleStop}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') handleStop(event);
          }}
        >
          <i className="fas fa-stop" aria-hidden="true" />
        </span>
      ) : null}
      {expanded ? (
        <span
          role="button"
          tabIndex={0}
          className="alty-chat-delete"
          title="Delete chat"
          onClick={handleDelete}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') handleDelete(event);
          }}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </span>
      ) : null}
    </button>
  );
}

const ChatRow = memo(ChatRowImpl);

function arrangeChatTree(chats: AltYChatSummary[]): AltYChatSummary[] {
  const byId = new Map(chats.map((chat) => [chat.runId, chat] as const));
  const children = new Map<number, AltYChatSummary[]>();
  const roots: AltYChatSummary[] = [];
  for (const chat of chats) {
    const parentId = typeof chat.parentRunId === 'number' ? chat.parentRunId : null;
    if (parentId && byId.has(parentId)) {
      const list = children.get(parentId) || [];
      list.push(chat);
      children.set(parentId, list);
    } else {
      roots.push(chat);
    }
  }
  const activity = (chat: AltYChatSummary) => {
    const own = new Date(chat.lastActivity).getTime() || 0;
    const childMax = Math.max(0, ...(children.get(chat.runId) || []).map((child) => new Date(child.lastActivity).getTime() || 0));
    return Math.max(own, childMax);
  };
  // Pin shared chats (the current viewer is a participant, not the owner) to
  // the top of the list — within each group, sort by most-recent activity.
  roots.sort((a, b) => {
    const sa = a.shared ? 0 : 1;
    const sb = b.shared ? 0 : 1;
    if (sa !== sb) return sa - sb;
    return activity(b) - activity(a);
  });
  for (const list of children.values()) {
    list.sort((a, b) => (new Date(b.lastActivity).getTime() || 0) - (new Date(a.lastActivity).getTime() || 0));
  }
  const ordered: AltYChatSummary[] = [];
  for (const root of roots) {
    ordered.push(root);
    ordered.push(...(children.get(root.runId) || []));
  }
  return ordered;
}

// Sidebar folder. A header (caret + label + count + actions) above the chat
// rows that belong to this group. Accepts dropped chat rows; the parent owns
// drag/drop bookkeeping so dropping in any region routes through one handler.
type ChatGroupSectionProps = {
  group: AltYChatGroup;
  rows: AltYChatSummary[];
  collapsed: boolean;
  editing: boolean;
  dragOver: boolean;
  onToggleCollapsed: (groupId: number) => void;
  onStartRename: (groupId: number) => void;
  onCancelRename: () => void;
  onCommitRename: (group: AltYChatGroup, label: string) => void;
  onDelete: (group: AltYChatGroup) => void;
  onNewChat: (group: AltYChatGroup) => void;
  onDragEnter: (groupId: number) => void;
  onDragLeave: (groupId: number) => void;
  onDrop: (groupId: number, event: React.DragEvent) => void;
  children: React.ReactNode;
};

function ChatGroupSection({
  group,
  rows,
  collapsed,
  editing,
  dragOver,
  onToggleCollapsed,
  onStartRename,
  onCancelRename,
  onCommitRename,
  onDelete,
  onNewChat,
  onDragEnter,
  onDragLeave,
  onDrop,
  children,
}: ChatGroupSectionProps) {
  const headerClass = [
    'alty-chat-group-header',
    collapsed ? 'collapsed' : '',
    dragOver ? 'drop-target' : '',
  ].filter(Boolean).join(' ');
  return (
    <div
      className={`alty-chat-group${dragOver ? ' drop-target' : ''}`}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = 'move'; }}
      onDragEnter={() => onDragEnter(group.id)}
      onDragLeave={(event) => {
        // Ignore leaves that just cross to a child element; only clear when the
        // pointer actually exits the section.
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
        onDragLeave(group.id);
      }}
      onDrop={(event) => onDrop(group.id, event)}
    >
      <div className={headerClass}>
        <button
          type="button"
          className="alty-chat-group-caret"
          title={collapsed ? 'Expand group' : 'Collapse group'}
          aria-label={collapsed ? 'Expand group' : 'Collapse group'}
          onClick={() => onToggleCollapsed(group.id)}
        >
          <i className={`fas ${collapsed ? 'fa-caret-right' : 'fa-caret-down'}`} aria-hidden="true" />
        </button>
        <span className="alty-chat-group-icon" aria-hidden="true">
          <i className={`fas ${collapsed ? 'fa-folder' : 'fa-folder-open'}`} />
        </span>
        {editing ? (
          <input
            className="alty-chat-group-rename"
            autoFocus
            defaultValue={group.label}
            maxLength={160}
            onBlur={(event) => onCommitRename(group, event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') (event.target as HTMLInputElement).blur();
              else if (event.key === 'Escape') onCancelRename();
            }}
            onClick={(event) => event.stopPropagation()}
          />
        ) : (
          <span
            className="alty-chat-group-label"
            onDoubleClick={(event) => { event.stopPropagation(); onStartRename(group.id); }}
            title="Double-click to rename"
          >
            {group.label}
          </span>
        )}
        <span className="alty-chat-group-count" aria-hidden="true">{rows.length}</span>
        <span
          role="button"
          tabIndex={0}
          className="alty-chat-group-action alty-chat-group-add"
          title={`New chat in “${group.label}”`}
          aria-label={`New chat in ${group.label}`}
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); onNewChat(group); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onNewChat(group);
            }
          }}
        >
          <i className="fas fa-plus" aria-hidden="true" />
        </span>
        <span
          role="button"
          tabIndex={0}
          className="alty-chat-group-action"
          title="Rename group"
          aria-label="Rename group"
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); onStartRename(group.id); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onStartRename(group.id);
            }
          }}
        >
          <i className="fas fa-pen" aria-hidden="true" />
        </span>
        <span
          role="button"
          tabIndex={0}
          className="alty-chat-group-action alty-chat-group-delete"
          title="Delete group"
          aria-label="Delete group"
          onClick={(event) => { event.preventDefault(); event.stopPropagation(); onDelete(group); }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              event.stopPropagation();
              onDelete(group);
            }
          }}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </span>
      </div>
      {collapsed ? null : (
        <div className="alty-chat-group-body">
          {rows.length === 0 ? (
            <div className="alty-chat-group-empty">Drag chats here</div>
          ) : children}
        </div>
      )}
    </div>
  );
}

function ToolMessage({ message, onExpandOutput }: {
  message: AltYChatMessage;
  onExpandOutput?: (message: AltYChatMessage) => void;
}) {
  const status = message.toolStatus || 'running';
  const icon = status === 'error' ? 'fa-exclamation-triangle' : status === 'done' ? 'fa-check' : 'fa-spinner fa-spin';
  const truncated = !!message.toolOutputTruncated;
  const [loadingOutput, setLoadingOutput] = useState(false);
  useEffect(() => {
    if (!truncated) setLoadingOutput(false);
  }, [truncated]);
  const handleToggle = (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (event.currentTarget.open && truncated && onExpandOutput && message.toolUseId && !loadingOutput) {
      setLoadingOutput(true);
      onExpandOutput(message);
    }
  };

  // Render every tool card collapsed by default — terminal-style one-line
  // entries that the user can click to inspect. Keeps the chat log scannable
  // when a turn fires twenty tools in a row.
  return (
    <details className={`alty-tool-card ${status}`} onToggle={handleToggle}>
      <summary>
        <span className="alty-tool-status"><i className={`fas ${icon}`} aria-hidden="true" /></span>
        <span className="alty-tool-name">{message.toolName || 'tool'}</span>
        <span className="alty-tool-summary">{message.toolSummary || status}</span>
      </summary>
      {message.toolInput ? (
        <>
          <div className="alty-tool-label">input</div>
          <pre>{message.toolInput}</pre>
        </>
      ) : null}
      {message.toolOutput || truncated ? (
        <>
          <div className="alty-tool-label">output{loadingOutput ? ' · loading...' : truncated ? ' · preview' : ''}</div>
          <pre>{message.toolOutput}</pre>
        </>
      ) : null}
    </details>
  );
}

function formatActivityTime(ts: number): string {
  const date = new Date(ts);
  if (!Number.isFinite(date.getTime())) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function ActivityPanel({ activities, busy }: { activities: AltYActivityEntry[]; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const latest = activities[activities.length - 1];
  const visible = activities.slice(-80);
  const statusIcon = busy ? 'fa-spinner fa-spin' : 'fa-tasks';
  useLayoutEffect(() => {
    if (!open) return;
    const list = listRef.current;
    if (!list) return;
    list.scrollTop = list.scrollHeight;
  }, [open, visible.length]);
  return (
    <section className={open ? 'alty-activity-panel open' : 'alty-activity-panel'} aria-label="Agent activity">
      <button
        type="button"
        className="alty-activity-summary"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="alty-activity-icon"><i className={`fas ${statusIcon}`} aria-hidden="true" /></span>
        <span className="alty-activity-title">Activity</span>
        <span className="alty-activity-latest">{latest?.label || 'No activity yet'}</span>
        <i className={`fas ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`} aria-hidden="true" />
      </button>
      {open ? (
        <div className="alty-activity-list" ref={listRef}>
          {visible.map((entry) => {
            const icon = entry.level === 'error'
              ? 'fa-exclamation-triangle'
              : entry.level === 'done'
                ? 'fa-check'
                : entry.level === 'tool'
                  ? 'fa-wrench'
                  : 'fa-circle';
            return (
              <div key={entry.id} className={`alty-activity-row ${entry.level || 'info'}`}>
                <span className="alty-activity-row-icon"><i className={`fas ${icon}`} aria-hidden="true" /></span>
                <span className="alty-activity-time">{formatActivityTime(entry.ts)}</span>
                <span className="alty-activity-copy">
                  <span className="alty-activity-label">{entry.label}</span>
                  {entry.provider ? <span className="alty-activity-provider">{entry.provider}</span> : null}
                  {entry.detail ? <span className="alty-activity-detail">{entry.detail}</span> : null}
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

const BEBOP_WORDS = [
  "Beboppin'", "Cogitating", "Pondering", "Brewing", "Mulling", "Conjuring",
  "Plotting", "Synthesizing", "Riffin'", "Vibing", "Noodlin'", "Tinkering",
  "Schemin'", "Computing", "Percolating", "Ruminating", "Reasoning",
  "Reflecting", "Contemplating", "Deliberating", "Considering", "Analyzing",
  "Investigating", "Exploring", "Spelunking", "Scouting", "Surveying",
  "Scanning", "Hunting", "Sleuthing", "Sniffing", "Tracking", "Probing",
  "Dissecting", "Examining", "Inspecting", "Wrangling", "Untangling",
  "Sketching", "Drafting", "Composing", "Crafting", "Forging", "Sculpting",
  "Weaving", "Stitching", "Knitting", "Bolting", "Hatching", "Cooking",
  "Stewing", "Simmering", "Marinating", "Steeping", "Distilling", "Filtering",
  "Refining", "Polishing", "Honing", "Sharpening", "Calibrating", "Tuning",
  "Wrestling", "Hustling", "Jammin'", "Improvising", "Freestyling",
  "Reckoning", "Hashing", "Crunching", "Grinding", "Churning", "Spinning",
  "Whisking", "Stirring", "Bubbling", "Fermenting", "Incubating",
  "Germinating", "Hatching ideas", "Connecting dots", "Untangling threads",
  "Following the trail", "Down the rabbit hole",
];

function formatElapsed(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return `${minutes}m ${seconds}s`;
  const hours = Math.floor(minutes / 60);
  const minRem = minutes % 60;
  return `${hours}h ${minRem}m`;
}

function formatTokens(n: number): string {
  if (n < 1000) return `${n}`;
  return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)}k`;
}

function pickBebopWord(prev: string | null): string {
  const pool = prev ? BEBOP_WORDS.filter((w) => w !== prev) : BEBOP_WORDS;
  return pool[Math.floor(Math.random() * pool.length)];
}

function ThinkingFooter({ startedAt, outputTokens, activeTool }: { startedAt: number; outputTokens: number; activeTool: string | null }) {
  const [now, setNow] = useState(() => Date.now());
  const [word, setWord] = useState<string>(() => pickBebopWord(null));
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    // Rotate to a different random word at a random 8-16s interval, so the
    // word feels like it's wandering rather than ticking on a metronome.
    let timer: number;
    const schedule = () => {
      const delay = 8000 + Math.random() * 8000;
      timer = window.setTimeout(() => {
        setWord((prev) => pickBebopWord(prev));
        schedule();
      }, delay);
    };
    schedule();
    return () => window.clearTimeout(timer);
  }, []);
  const elapsed = now - startedAt;
  const parts = [formatElapsed(elapsed)];
  if (outputTokens > 0) parts.push(`↓ ${formatTokens(outputTokens)} tokens`);
  if (activeTool) parts.push(activeTool);
  return (
    <div className="alty-thinking" role="status" aria-live="polite">
      <span className="alty-thinking-dot" aria-hidden="true" />
      <span className="alty-thinking-word">{word}…</span>
      <span className="alty-thinking-meta">({parts.join(' · ')})</span>
    </div>
  );
}

// The Customized BMad Agent Flow ends replies with a fenced ```json``` block of
// shape { "bmad_workflow": {...} } that drives the right-side workflow tabs.
// Hide that block from the chat bubble so the conversation stays readable — the
// content already lives in the tabs. Cheap guard first; other agents' json
// blocks (keyed differently) and any block that doesn't parse are left intact.
function stripBmadWorkflowBlock(text: string): string {
  if (!text.includes('bmad_workflow')) return text;
  return text
    .replace(/```json\s*([\s\S]*?)```/gi, (full, inner) => {
      try {
        const parsed = JSON.parse(inner);
        if (parsed && typeof parsed === 'object' && parsed.bmad_workflow) return '';
      } catch { /* not valid JSON yet (mid-stream) or unrelated — leave it */ }
      return full;
    })
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function CanvasWritingStatus() {
  return (
    <span className="alty-canvas-writing writing" role="status" aria-live="polite">
      <i className="fas fa-spinner fa-spin" aria-hidden="true" />
      <span>Drawing canvas</span>
    </span>
  );
}

function CanvasDoneStatus() {
  return (
    <span className="alty-canvas-writing done" role="status" aria-live="polite">
      <i className="fas fa-check" aria-hidden="true" />
      <span>Canvas drawn</span>
    </span>
  );
}

function MessageImpl({
  message,
  onRerun,
  onExpandToolOutput,
  canvasModeActive,
}: {
  message: AltYChatMessage;
  onRerun: (message: AltYChatMessage) => void;
  onExpandToolOutput: (message: AltYChatMessage) => void;
  canvasModeActive: boolean;
}) {
  const [copied, setCopied] = useState(false);
  if (message.role === 'tool') return <ToolMessage message={message} onExpandOutput={onExpandToolOutput} />;
  if (message.role === 'meta' || message.role === 'system') {
    return <div className="alty-message-meta"><MarkdownText text={message.text} /></div>;
  }
  const rawBody = message.text || (message.role === 'assistant' ? '...' : '');
  const canvasView = message.role === 'assistant'
    ? formatAssistantCanvasChat(stripBmadWorkflowBlock(rawBody), { canvasMode: canvasModeActive })
    : { chatText: rawBody, writingCanvas: false, canvasUpdated: false };
  const body = canvasView.chatText;
  // Copy the user-visible text (assistant: with structured blocks stripped).
  const copyText = message.role === 'assistant' ? body : message.text;
  const canCopy = !!copyText.trim();
  // Rerun only makes sense for a prompt — resend the user's message verbatim.
  const canRerun = message.role === 'user' && (!!message.text.trim() || !!message.images?.length);
  const handleCopy = () => {
    if (!canCopy) return;
    void copyTextToClipboard(copyText).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      },
      () => undefined,
    );
  };
  return (
    <div className={`alty-message ${message.role}`}>
      <div className="alty-message-bubble">
        {message.images?.length ? (
          <div className="alty-message-images">
            {message.images.map((attachment, index) => (
              attachment.mediaType.startsWith('image/') ? (
                <img
                  key={index}
                  src={`data:${attachment.mediaType};base64,${attachment.dataBase64}`}
                  alt={attachment.name || 'attached image'}
                />
              ) : (
                <span key={index} className="alty-message-file" title={attachment.name}>
                  <i className="fas fa-file" aria-hidden="true" />
                  <span>{attachment.name || 'file'}</span>
                </span>
              )
            ))}
          </div>
        ) : null}
        {body ? <MarkdownText text={body} /> : null}
        {canvasView.writingCanvas ? <CanvasWritingStatus /> : null}
        {!canvasView.writingCanvas && canvasView.canvasUpdated ? <CanvasDoneStatus /> : null}
      </div>
      {canCopy || canRerun ? (
        <div className="alty-message-actions">
          {canCopy ? (
            <button
              type="button"
              className="alty-message-action"
              title={copied ? 'Copied' : 'Copy message'}
              aria-label={copied ? 'Copied' : 'Copy message'}
              onClick={handleCopy}
            >
              <i className={copied ? 'fas fa-check' : 'fas fa-copy'} aria-hidden="true" />
            </button>
          ) : null}
          {canRerun ? (
            <button
              type="button"
              className="alty-message-action"
              title="Rerun this prompt"
              aria-label="Rerun this prompt"
              onClick={() => onRerun(message)}
            >
              <i className="fas fa-redo" aria-hidden="true" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// setMessages preserves identity for unchanged items (only the patched item
// gets a new reference via {...item, ...patch}), so memo with the default
// shallow compare actually skips the giant re-render storm when status / busy
// / thinking ticks fire.
const Message = memo(MessageImpl);

export function AltYPanel({
  isOpen,
  mobile = false,
  chat,
  appFrame,
  tabs,
  activeTabId,
  onSelectTab,
  pinnedAgents,
  selectedAgentId,
  selectedPinnedAgentId,
  onSelectPinnedAgent,
  onEditPinnedAgent,
  onDeletePinnedAgent,
  onStartNewChat,
  onStartSubchat,
  onBrokerRestartReady,
  improveAgentLabel,
  onImproveAgent,
  onShowApp,
  canReopenDiagram,
  onReopenDiagram,
  canShowIdeas,
  ideasOpen,
  onToggleIdeas,
  canShowApartments,
  apartmentsOpen,
  onToggleApartments,
  canShowBmadWorkflow,
  bmadWorkflowOpen,
  onToggleBmadWorkflow,
  canShowDiscovery,
  discoveryOpen,
  onToggleDiscovery,
  settingsOpen,
  onSettingsOpenChange,
  canvasOnly = false,
  onCanvasOnlyChange,
  canvasContent = null,
  canvasBadge = null,
  selectedUserIds,
  onSelectUserIds,
}: AltYPanelProps) {
  const userIds = selectedUserIds ?? [];
  const [showParticipants, setShowParticipants] = useState(false);
  const [width, setWidth] = useState(restoreWidth);
  const [draft, setDraft] = useState('');
  const [pendingImages, setPendingImages] = useState<AltYImageAttachment[]>([]);
  const [model, setModel] = useState<AltYModel>(readInitialModel);
  const [thinking, setThinking] = useState(readInitialThinking);
  const [fast, setFast] = useState(() => localStorage.getItem('altY.fast') === '1');
  const [maxMode, setMaxMode] = useState(() => localStorage.getItem('altY.max') === '1');
  // Models picked in addition to the primary one. When non-empty, the next send
  // is a consensus turn — the broker fans the prompt out into one subchat per
  // model and synthesises the verdict in the main chat.
  const [extraModels, setExtraModels] = useState<string[]>(readInitialExtraModels);
  const [modelsPopoverOpen, setModelsPopoverOpen] = useState(false);
  const modelsPopoverRef = useRef<HTMLDivElement | null>(null);
  const [showToolCalls, setShowToolCalls] = useState(() => localStorage.getItem('altY.showToolCalls') !== '0');
  const [showActivity, setShowActivity] = useState(() => localStorage.getItem('altY.showActivity') !== '0');
  // Speech-to-text. Keys + model live in localStorage; the cog in the header
  // edits them. Two providers, two keys: ElevenLabs (xi-api-key) for the
  // scribe_* models and Groq (Bearer) for the groq_whisper_* models.
  const [scribeApiKey, setScribeApiKey] = useState(() => localStorage.getItem('altY.scribe.apiKey') || '');
  const [scribeGroqApiKey, setScribeGroqApiKey] = useState(() => localStorage.getItem('altY.scribe.groqApiKey') || '');
  // BYOK keys for the cross-model MCP tools (openrouter / openai-chat). Loaded
  // from the broker user_settings on mount and re-saved (debounced) on edit, so
  // a key change applies on the next turn without any restart. Local cache is
  // only there so the inputs show the previous value across reloads while the
  // initial GET is in flight; the server is the source of truth.
  const [openrouterApiKey, setOpenrouterApiKey] = useState(() => localStorage.getItem('altY.openrouterApiKey') || '');
  const [openaiApiKey, setOpenaiApiKey] = useState(() => localStorage.getItem('altY.openaiApiKey') || '');
  const [scribeModel, setScribeModel] = useState<ScribeModel>(
    // Default for users with no stored preference (no localStorage value AND no
    // server-side scribeModel). Existing users keep their saved choice: a local
    // value wins on init, and the server value (fetched on mount) overrides this
    // fallback when present — so only brand-new users land on Whisper v3 Turbo.
    () => (localStorage.getItem('altY.scribe.model') as ScribeModel) || 'groq_whisper_v3_turbo',
  );
  // Which provider the selected model uses, and the key that goes with it — so
  // the gating/UI below stay model-agnostic.
  const scribeIsGroq = isGroqScribeModel(scribeModel);
  const scribeActiveKey = scribeIsGroq ? scribeGroqApiKey : scribeApiKey;
  // Spoken-language hint for transcription; 'auto' (the default) lets ElevenLabs
  // detect it. Persisted alongside the key + model.
  const [scribeLanguage, setScribeLanguage] = useState(
    () => localStorage.getItem('altY.scribe.language') || SCRIBE_LANGUAGE_AUTO,
  );
  // When on, a dictation session that finishes transcribing auto-sends the
  // composer instead of waiting for the Send button. Client-only preference,
  // defaulting ON — unset reads as enabled; only an explicit '0' opts out.
  const [autoSubmit, setAutoSubmit] = useState(() => localStorage.getItem('altY.scribe.autoSubmit') !== '0');
  // One-time nudge: the first time a SuperAdmin sends a message with auto-submit
  // (now the default) still on, point them at the cog so they know it's there
  // and can switch it off. Shown once per account, then remembered.
  const [autoSubmitTipSeen, setAutoSubmitTipSeen] = useState(() => localStorage.getItem('altY.autoSubmitTipSeen') === '1');
  const [autoSubmitTip, setAutoSubmitTip] = useState(false);
  // Set true by the scribe onDone callback; an effect below performs the send
  // once the final transcript has flushed into the draft.
  const [autoSubmitArmed, setAutoSubmitArmed] = useState(false);
  // Uncontrolled by default (desktop), controlled when App passes the flag
  // (mobile) so the app-view bar's cog can open this same popover.
  const [scribeSettingsOpenInternal, setScribeSettingsOpenInternal] = useState(false);
  const scribeSettingsOpen = settingsOpen ?? scribeSettingsOpenInternal;
  const setScribeSettingsOpen = useCallback((open: boolean) => {
    if (onSettingsOpenChange) onSettingsOpenChange(open);
    else setScribeSettingsOpenInternal(open);
  }, [onSettingsOpenChange]);
  const scribeSettingsRef = useRef<HTMLDivElement | null>(null);
  const scribeKeyInputRef = useRef<HTMLInputElement | null>(null);
  // Set when the user clicked the mic with no key saved: we auto-open the
  // settings popover and pulse/focus the key field so it's obvious where the
  // key goes. Cleared whenever the popover closes (effect below).
  const [scribeKeyNudge, setScribeKeyNudge] = useState(false);
  // Flips true once the per-superadmin settings have been fetched from the
  // broker, gating the debounced save effect so it can't clobber the stored
  // key with the localStorage cache before we've read it.
  const scribeServerLoadedRef = useRef(false);
  // The owner (SuperAdmin email) the broker is storing this key under — shown
  // in the settings hint so it's clear WHICH account holds the key (and that
  // desktop + mobile resolve to the same one). Empty until the server replies.
  const [scribeOwner, setScribeOwner] = useState('');
  // Per-key save status surfaced next to each API-key input as a small
  // "Saving… / ✓ Saved" badge so pasting a key gives visible confirmation.
  // onChange flips to 'saving'; the debounced PUT's .then flips to 'saved';
  // a 2.5s timer drops back to 'idle'. Refs mirror state so the .then can read
  // the current status without a stale closure.
  type KeySaveStatus = 'idle' | 'saving' | 'saved';
  const [scribeKeySaveStatus, setScribeKeySaveStatus] = useState<KeySaveStatus>('idle');
  const [openrouterKeySaveStatus, setOpenrouterKeySaveStatus] = useState<KeySaveStatus>('idle');
  const [openaiKeySaveStatus, setOpenaiKeySaveStatus] = useState<KeySaveStatus>('idle');
  const scribeKeySaveStatusRef = useRef<KeySaveStatus>('idle');
  const openrouterKeySaveStatusRef = useRef<KeySaveStatus>('idle');
  const openaiKeySaveStatusRef = useRef<KeySaveStatus>('idle');
  scribeKeySaveStatusRef.current = scribeKeySaveStatus;
  openrouterKeySaveStatusRef.current = openrouterKeySaveStatus;
  openaiKeySaveStatusRef.current = openaiKeySaveStatus;
  const renderKeySaveBadge = (status: KeySaveStatus) => {
    if (status === 'idle') return null;
    return (
      <span className={`alty-key-save-status ${status}`}>
        {status === 'saving' ? (
          <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Saving…</>
        ) : (
          <><i className="fas fa-check" aria-hidden="true" /> Saved</>
        )}
      </span>
    );
  };
  // True when the broker is running older code than what's on disk (an Alt-Y
  // dev turn edited it). Drives the amber ⚙ cog + "Restart Alt-Y server" button.
  const [brokerRestartNeeded, setBrokerRestartNeeded] = useState(false);
  const [brokerRestarting, setBrokerRestarting] = useState(false);
  // True when a newer Alt-Y app bundle has been deployed than the one running
  // here (deployed build id ≠ our baked-in id). Drives the same amber ⚙ cog +
  // badge as the restart flag, plus a "Reload app" button in the cog menu.
  const [appUpdateAvailable, setAppUpdateAvailable] = useState(false);
  const [chatDetails, setChatDetails] = useState<ChatDetailsState | null>(null);
  const chatDetailsRequestRef = useRef(0);
  const activeChatTitleRef = useRef<HTMLButtonElement | null>(null);
  const chatInfoRef = useRef<HTMLElement | null>(null);
  const [titleEditing, setTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [titleSaving, setTitleSaving] = useState(false);
  const [titleError, setTitleError] = useState('');
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  const appendTranscript = useCallback((text: string) => {
    setDraft((prev) => (prev.trim() ? `${prev.replace(/\s+$/, '')} ${text}` : text));
  }, []);
  // Read in the onDone callback (which is bound once) so toggling Auto-submit
  // takes effect without re-creating the scribe controller.
  const autoSubmitRef = useRef(autoSubmit);
  autoSubmitRef.current = autoSubmit;
  const autoSubmitTipSeenRef = useRef(autoSubmitTipSeen);
  autoSubmitTipSeenRef.current = autoSubmitTipSeen;
  const handleScribeDone = useCallback(() => {
    if (autoSubmitRef.current) setAutoSubmitArmed(true);
  }, []);
  const scribe = useScribe({ apiKey: scribeApiKey, groqApiKey: scribeGroqApiKey, model: scribeModel, language: scribeLanguage, onText: appendTranscript, onDone: handleScribeDone });
  // The mic button's hover/long-press tooltip (CSS ::after on data-tooltip) is
  // unwanted on touch viewports — omit the attribute there so neither the
  // start nor stop hint renders. Desktop keeps both tooltips.
  const isMobile = useIsMobile();
  // Toggling the mic (clicking it): with no key yet, open Settings, pulse the
  // key field, and put the cursor in it so it's obvious what to do; otherwise
  // start/stop transcribing.
  const handleMicToggle = useCallback(() => {
    if (scribe.busy) return;
    if (!scribe.recording && !scribe.starting && !scribeActiveKey.trim()) {
      setScribeSettingsOpen(true);
      setScribeKeyNudge(true);
      window.setTimeout(() => scribeKeyInputRef.current?.focus(), 60);
      return;
    }
    scribe.toggle();
  }, [scribe, scribeActiveKey]);
  // Push-to-talk: hold Right Shift to record, release to stop — so the user can
  // just hold it down while talking. Start, with no key yet, nudges Settings
  // exactly like clicking the mic does. Returns whether it actually began a new
  // recording, so a stray Right Shift while a click-started recording is in
  // progress doesn't stop it on release.
  const startPushToTalk = useCallback((): boolean => {
    if (scribe.busy || scribe.recording || scribe.starting) return false;
    if (!scribeActiveKey.trim()) {
      setScribeSettingsOpen(true);
      setScribeKeyNudge(true);
      window.setTimeout(() => scribeKeyInputRef.current?.focus(), 60);
      return false;
    }
    scribe.toggle(); // idle + not busy → starts recording
    return true;
  }, [scribe, scribeActiveKey]);
  const stopPushToTalk = useCallback(() => {
    scribe.stop();
  }, [scribe]);
  // Refs keep the always-latest handlers so the document listener binds once.
  const startPushToTalkRef = useRef(startPushToTalk);
  startPushToTalkRef.current = startPushToTalk;
  const stopPushToTalkRef = useRef(stopPushToTalk);
  stopPushToTalkRef.current = stopPushToTalk;
  useEffect(() => {
    // Right Shift held on its own drives push-to-talk. We defer the actual
    // start briefly so using Right Shift as a modifier (e.g. to type a capital)
    // cancels before we ever touch the mic; any other key pressed while it's
    // down marks a chord and abandons the pending start.
    let phase: 'idle' | 'pending' | 'recording' = 'idle';
    let weStarted = false;
    let startTimer: number | null = null;
    const clearStart = () => {
      if (startTimer != null) { window.clearTimeout(startTimer); startTimer = null; }
    };
    const finish = () => {
      clearStart();
      if (phase === 'recording' && weStarted) stopPushToTalkRef.current();
      phase = 'idle';
      weStarted = false;
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.code === 'ShiftRight') {
        if (event.repeat || phase !== 'idle') return;
        phase = 'pending';
        startTimer = window.setTimeout(() => {
          startTimer = null;
          phase = 'recording';
          weStarted = startPushToTalkRef.current();
        }, 140);
        return;
      }
      if (phase === 'pending') { clearStart(); phase = 'idle'; } // chord, not a hold
    };
    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === 'ShiftRight') finish();
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('keyup', onKeyUp);
    // A held Right Shift that loses focus (alt-tab) never fires keyup — stop so
    // the recording can't get stuck on.
    window.addEventListener('blur', finish);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', finish);
    };
  }, []);
  // Mobile-only sidebar drawer. Closed by default so the panel opens on the
  // conversation (Claude/ChatGPT mobile pattern); the hamburger opens it as a
  // full-width overlay. Desktop ignores this and keeps the icon-rail sidebar.
  const [drawerOpen, setDrawerOpen] = useState(false);
  // Tap-through guard. The hamburger that opens the drawer and the drawer's
  // close-X / backdrop occupy the same top-left spot. On touch, the single tap
  // that opens the drawer "bleeds through" onto the close-X that now sits under
  // the finger (a well-known iOS/Android ghost-click once the layout shifts),
  // slamming the drawer shut the instant it opens. Stamp the open time and have
  // the close affordances ignore any close fired within a short window of it.
  const drawerOpenedAtRef = useRef(0);
  const openDrawer = useCallback(() => {
    drawerOpenedAtRef.current = Date.now();
    setDrawerOpen(true);
  }, []);
  const closeDrawer = useCallback(() => {
    if (Date.now() - drawerOpenedAtRef.current < 350) return;
    setDrawerOpen(false);
  }, []);
  // The full-width mobile drawer always shows labels (it's not a slim rail),
  // so treat it as expanded regardless of the desktop sidebarExpanded pref.
  const expandedView = mobile || chat.sidebarExpanded;
  // Mobile only: track the visual viewport so the panel pins to the region
  // visible above the soft keyboard (header stays at the top). Desktop passes
  // enabled=false → { height: null } and uses the dvh fallback in CSS.
  const viewport = useVisualViewport(mobile);

  // Sidebar chat search. The input filters the chat list; an intelligent
  // (semantic) ranking comes from the broker's Cerebras-backed /search-chats
  // (best match first), with an instant local substring filter as the
  // always-available fallback while that call is in flight or unavailable.
  const [chatSearch, setChatSearch] = useState('');
  // The Cerebras ranking, keyed to the exact query it was computed for so a
  // stale result for a previous query is never applied.
  const [searchMatches, setSearchMatches] = useState<{ query: string; order: number[] } | null>(null);
  const [searching, setSearching] = useState(false);
  // Read the latest chats at call time without re-firing the search on every
  // 4s background list refresh — only the query change (debounced) triggers it.
  const chatsForSearchRef = useRef(chat.chats);
  chatsForSearchRef.current = chat.chats;
  useEffect(() => {
    const query = chatSearch.trim();
    if (!query) {
      setSearchMatches(null);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const timer = window.setTimeout(() => {
      const slim = chatsForSearchRef.current.map((c) => ({
        runId: c.runId,
        title: c.title || '',
        subtitle: c.subtitle || '',
        lastTurnSummary: c.lastTurnSummary || '',
      }));
      void searchAltYChats(query, slim).then((order) => {
        if (cancelled) return;
        setSearching(false);
        // null → the broker call failed (or the route isn't live yet); keep
        // the local substring fallback rather than clobbering with nothing.
        if (order) setSearchMatches({ query, order });
      });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [chatSearch]);

  // Sidebar folder collapse-state, persisted across reloads. Closed groups stay
  // closed when the chat list refreshes; an unknown group id defaults to open.
  const [collapsedGroupIds, setCollapsedGroupIds] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem('altY.collapsedGroups.v1');
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr.filter((v) => typeof v === 'number') : []);
    } catch { return new Set(); }
  });
  useEffect(() => {
    try {
      localStorage.setItem('altY.collapsedGroups.v1', JSON.stringify([...collapsedGroupIds]));
    } catch { /* quota — ignore */ }
  }, [collapsedGroupIds]);
  const toggleGroupCollapsed = useCallback((groupId: number) => {
    setCollapsedGroupIds((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  // Drag-and-drop target highlight. -1 stands for "the ungrouped section" so a
  // chat can be dropped out of a folder back to the flat list.
  const [dragOverGroupId, setDragOverGroupId] = useState<number | null>(null);
  const [draggingChatRunId, setDraggingChatRunId] = useState<number | null>(null);

  // Inline rename state for a single group at a time.
  const [editingGroupId, setEditingGroupId] = useState<number | null>(null);

  const moveChatToGroup = chat.moveChatToGroup;
  const createGroup = chat.createGroup;
  const renameGroup = chat.renameGroup;
  const removeGroup = chat.removeGroup;

  const handleCreateGroup = useCallback(() => {
    void (async () => {
      // Inline naming via a prompt — keeps the affordance discoverable without
      // a heavyweight modal. The placeholder is the suggested label.
      const raw = window.prompt('Name the new group:', 'New group');
      if (raw == null) return;
      const label = raw.trim();
      if (!label) return;
      const created = await createGroup(label.slice(0, 160));
      if (created) {
        // New groups start expanded — drop any stale collapsed entry just in
        // case a previous group with the same id had been collapsed.
        setCollapsedGroupIds((prev) => {
          if (!prev.has(created.id)) return prev;
          const next = new Set(prev);
          next.delete(created.id);
          return next;
        });
      }
    })();
  }, [createGroup]);

  const handleGroupRename = useCallback((group: AltYChatGroup, label: string) => {
    const trimmed = label.trim();
    if (!trimmed || trimmed === group.label) {
      setEditingGroupId(null);
      return;
    }
    void renameGroup(group.id, trimmed).finally(() => setEditingGroupId(null));
  }, [renameGroup]);

  const handleGroupDelete = useCallback((group: AltYChatGroup) => {
    if (!window.confirm(`Delete group “${group.label}”?\n\nChats inside it move back to the main list.`)) return;
    void removeGroup(group.id);
  }, [removeGroup]);

  // "+" on a group header: start a fresh chat AND drop it into that group in
  // one step, so the user doesn't have to create-then-drag. Uses the chat
  // hook's own startNewChat (which returns the new runId) rather than the
  // App-level onStartNewChat prop — that one is fire-and-forget and the host
  // would race with us setting the group.
  const handleGroupNewChat = useCallback((group: AltYChatGroup) => {
    void (async () => {
      // Expand the group so the user sees their new chat appear.
      setCollapsedGroupIds((prev) => {
        if (!prev.has(group.id)) return prev;
        const next = new Set(prev);
        next.delete(group.id);
        return next;
      });
      const runId = await chat.startNewChat();
      if (typeof runId === 'number') {
        await moveChatToGroup(runId, group.id);
      }
      setDrawerOpen(false);
    })();
  }, [chat, moveChatToGroup]);

  const handleChatDragStart = useCallback((runId: number, event: React.DragEvent) => {
    setDraggingChatRunId(runId);
    event.dataTransfer.effectAllowed = 'move';
    try { event.dataTransfer.setData('text/x-alty-chat', String(runId)); } catch { /* Safari quirks */ }
  }, []);
  const handleChatDragEnd = useCallback(() => {
    setDraggingChatRunId(null);
    setDragOverGroupId(null);
  }, []);
  const acceptChatDrop = useCallback((groupId: number | null, event: React.DragEvent) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData('text/x-alty-chat');
    const runId = Number.parseInt(raw, 10);
    setDragOverGroupId(null);
    setDraggingChatRunId(null);
    if (!Number.isFinite(runId)) return;
    void moveChatToGroup(runId, groupId);
  }, [moveChatToGroup]);

  // Stable bound handlers passed to memoized ChatRow — see ChatRowImpl comment.
  const attachChat = chat.attachChat;
  const stopChat = chat.stopChat;
  const removeChat = chat.removeChat;
  const handleChatSelect = useCallback((runId: number) => {
    void attachChat(runId);
    setDrawerOpen(false);
  }, [attachChat]);
  const handleStartSubchat = useCallback(() => {
    void (async () => {
      const started = onStartSubchat ? await onStartSubchat() : await chat.startSubchat();
      const runId = typeof started === 'number' ? started : null;
      if (runId != null) {
        setChatSelection(runId, { agentId: selectedAgentId, model, thinking, fast, max: maxMode });
      }
      setDrawerOpen(false);
    })();
  }, [chat, onStartSubchat, selectedAgentId, model, thinking, fast, maxMode]);
  const handleChatStop = useCallback((runId: number) => {
    stopChat(runId);
  }, [stopChat]);
  const handleChatDelete = useCallback((runId: number, title: string) => {
    if (window.confirm(`Delete this chat?\n\n${title}`)) void removeChat(runId);
  }, [removeChat]);
  // Editing an agent surfaces its diagram. On mobile that diagram is a
  // full-screen overlay, so close the drawer first — otherwise closing the
  // diagram later drops the user back onto the open drawer instead of the
  // conversation. A no-op on desktop (no drawer).
  const handleEditPinnedAgent = useCallback((id: string) => {
    setDrawerOpen(false);
    onEditPinnedAgent(id);
  }, [onEditPinnedAgent]);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const messagesRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  // Auto-scroll only when the user is already pinned to the bottom; otherwise
  // streaming text yanks them out of whatever they scrolled up to re-read.
  const pinnedToBottomRef = useRef(true);
  // chat state lives in App.tsx now — lifted so the Agent Factory canvas can read it too.

  useEffect(() => {
    window.AltY?.registerReactPanel?.({
      open: () => textareaRef.current?.focus(),
      close: () => textareaRef.current?.blur(),
      focus: () => textareaRef.current?.focus(),
    });
    return () => window.AltY?.registerReactPanel?.(null);
  }, []);

  // Mobile only: stop the document from scrolling while the full-bleed panel
  // is mounted. Without this the browser scrolls the focused textarea into
  // view when the keyboard opens and drags the whole fixed panel up, taking
  // the header off the top. Restored on unmount / when leaving mobile.
  useEffect(() => {
    if (!mobile) return;
    const body = document.body;
    const root = document.documentElement;
    const prev = {
      bodyOverflow: body.style.overflow,
      bodyOverscroll: body.style.overscrollBehavior,
      rootOverflow: root.style.overflow,
    };
    body.style.overflow = 'hidden';
    body.style.overscrollBehavior = 'none';
    root.style.overflow = 'hidden';
    return () => {
      body.style.overflow = prev.bodyOverflow;
      body.style.overscrollBehavior = prev.bodyOverscroll;
      root.style.overflow = prev.rootOverflow;
    };
  }, [mobile]);

  useLayoutEffect(() => {
    // On mobile the panel is full-bleed — there's no iframe alongside it, so
    // don't reserve any width (that would push content off-screen).
    if (mobile) {
      window.AltY?.setReservedWidth?.(0);
      return;
    }
    const reserved = isOpen ? clampWidth(width) : 0;
    window.AltY?.setReservedWidth?.(reserved);
    return () => window.AltY?.setReservedWidth?.(0);
  }, [isOpen, width, mobile]);

  useEffect(() => {
    if (!isOpen) return;
    window.setTimeout(() => textareaRef.current?.focus(), 80);
  }, [isOpen]);

  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    const onScroll = () => {
      pinnedToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!pinnedToBottomRef.current) return;
    messagesRef.current?.scrollTo({ top: messagesRef.current.scrollHeight });
  }, [chat.messages, chat.activities.length]);

  useEffect(() => {
    localStorage.setItem('altY.model', model);
  }, [model]);
  useEffect(() => {
    localStorage.setItem('altY.thinking', thinking);
  }, [thinking]);
  useEffect(() => {
    localStorage.setItem('altY.fast', fast ? '1' : '0');
  }, [fast]);
  useEffect(() => {
    localStorage.setItem('altY.max', maxMode ? '1' : '0');
  }, [maxMode]);
  useEffect(() => {
    localStorage.setItem('altY.extraModels.v1', JSON.stringify(extraModels));
  }, [extraModels]);

  // Close the models popover on outside click or Escape.
  useEffect(() => {
    if (!modelsPopoverOpen) return;
    function onPointerDown(event: PointerEvent) {
      const node = modelsPopoverRef.current;
      if (node && !node.contains(event.target as Node)) setModelsPopoverOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setModelsPopoverOpen(false);
    }
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [modelsPopoverOpen]);

  // --- Per-chat agent + model memory -------------------------------------
  // The agent picker + model selection must NOT drift on their own. They change
  // only when the user picks one, or when the user switches to a PREVIOUS chat —
  // in which case we restore the agent + model that chat was last used with.
  //
  // Record: snapshot the live selection the moment the user actually sends in a
  // chat (a fresh user bubble appears), keyed by the runId the message landed
  // in. `justSentRef` gates out transcript replay, which re-adds user bubbles
  // when attaching an existing chat but isn't a real send.
  const justSentRef = useRef(false);
  const recordedUserMsgIdRef = useRef<string | null>(null);
  // Stable wrapper around the rerun logic (assigned below in render) so the
  // prop passed to memoized <Message> rows never changes identity.
  const rerunMessageRef = useRef<(message: AltYChatMessage) => void>(() => undefined);
  const handleRerunMessage = useCallback((message: AltYChatMessage) => {
    rerunMessageRef.current(message);
  }, []);
  const loadToolOutput = chat.loadToolOutput;
  const handleExpandToolOutput = useCallback((message: AltYChatMessage) => {
    if (message.toolUseId) void loadToolOutput(message.id, message.toolUseId);
  }, [loadToolOutput]);
  useEffect(() => {
    if (!justSentRef.current) return;
    const runId = chat.activeRunId;
    if (runId == null) return;
    let lastUserId: string | null = null;
    for (let i = chat.messages.length - 1; i >= 0; i -= 1) {
      if (chat.messages[i].role === 'user') {
        lastUserId = chat.messages[i].id;
        break;
      }
    }
    if (!lastUserId || lastUserId === recordedUserMsgIdRef.current) return;
    recordedUserMsgIdRef.current = lastUserId;
    justSentRef.current = false;
    setChatSelection(runId, { agentId: selectedAgentId, model, thinking, fast, max: maxMode, extraModels });
  }, [chat.messages, chat.activeRunId, selectedAgentId, model, thinking, fast, maxMode, extraModels]);

  // Restore: when the active chat changes to one we remember, snap the model
  // (and the agent, via onSelectPinnedAgent) back to what it was last used with.
  // No memory for the chat (a brand-new chat, or one started before this
  // existed) → leave the current selection untouched. Runs on every activeRunId
  // change; a new chat / reconnect simply has nothing to restore.
  const restoreAgentRef = useRef(onSelectPinnedAgent);
  restoreAgentRef.current = onSelectPinnedAgent;
  const pinnedAgentsRef = useRef(pinnedAgents);
  pinnedAgentsRef.current = pinnedAgents;
  useEffect(() => {
    const runId = chat.activeRunId;
    if (runId == null) return;
    const sel = getChatSelection(runId);
    if (!sel) return;
    if (sel.model && isAltYModel(sel.model)) setModel(sel.model);
    if (sel.thinking && THINKING_LABELS[sel.thinking]) setThinking(sel.thinking);
    if (typeof sel.fast === 'boolean') setFast(sel.fast);
    if (typeof sel.max === 'boolean') setMaxMode(sel.max);
    if (Array.isArray(sel.extraModels)) {
      const valid = new Set(allModelOptionValues());
      setExtraModels(sel.extraModels.filter((v): v is string => typeof v === 'string' && valid.has(v)));
    }
    if (
      sel.agentId &&
      (sel.agentId === 'auto' || pinnedAgentsRef.current.some((a) => a.id === sel.agentId))
    ) {
      restoreAgentRef.current(sel.agentId);
    }
    // Only re-run on chat switch; the refs above keep the latest callback/list
    // without widening the trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chat.activeRunId]);

  useEffect(() => {
    localStorage.setItem('altY.showToolCalls', showToolCalls ? '1' : '0');
  }, [showToolCalls]);
  useEffect(() => {
    localStorage.setItem('altY.showActivity', showActivity ? '1' : '0');
  }, [showActivity]);

  useEffect(() => {
    localStorage.setItem('altY.scribe.autoSubmit', autoSubmit ? '1' : '0');
  }, [autoSubmit]);

  // Auto-submit: when scribe.onDone armed this, the final transcript has
  // already flushed into `draft` (onText runs before onDone), so this render
  // sees the complete text. Fire send() once and disarm. send() no-ops on an
  // empty draft, so a recording that produced nothing won't submit.
  useEffect(() => {
    if (!autoSubmitArmed) return;
    setAutoSubmitArmed(false);
    send();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSubmitArmed]);

  // localStorage is now just a fast-paint cache; the broker (per-superadmin) is
  // the source of truth so the key/model follow the user across browsers.
  useEffect(() => {
    localStorage.setItem('altY.scribe.apiKey', scribeApiKey);
  }, [scribeApiKey]);

  useEffect(() => {
    localStorage.setItem('altY.scribe.groqApiKey', scribeGroqApiKey);
  }, [scribeGroqApiKey]);

  useEffect(() => {
    localStorage.setItem('altY.scribe.model', scribeModel);
  }, [scribeModel]);

  useEffect(() => {
    localStorage.setItem('altY.scribe.language', scribeLanguage);
  }, [scribeLanguage]);

  // Load the server-stored settings once on mount; they win over the local
  // cache. If the server has nothing yet but this browser has a cached key
  // (e.g. carried over from the old localStorage-only version), push it up so
  // existing users migrate transparently on first load.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const server = await getAltYSettings();
      if (cancelled) return;
      const serverKey = typeof server.scribeApiKey === 'string' ? server.scribeApiKey : null;
      const serverGroqKey = typeof server.scribeGroqApiKey === 'string' ? server.scribeGroqApiKey : null;
      const serverModel = typeof server.scribeModel === 'string' && server.scribeModel in SCRIBE_MODEL_LABELS
        ? server.scribeModel as ScribeModel : null;
      const serverLanguage = typeof server.scribeLanguage === 'string' ? server.scribeLanguage : null;
      const localAutoSubmitTipSeen = localStorage.getItem('altY.autoSubmitTipSeen') === '1';
      const serverAutoSubmitTipSeen = server.autoSubmitTipSeen === true;
      if (typeof server.owner === 'string') setScribeOwner(server.owner);
      if (serverKey) setScribeApiKey(serverKey);
      if (serverGroqKey) setScribeGroqApiKey(serverGroqKey);
      if (serverModel) setScribeModel(serverModel);
      if (serverLanguage) setScribeLanguage(serverLanguage);
      if (!serverKey && !serverGroqKey && (scribeApiKey.trim() || scribeGroqApiKey.trim())) {
        void updateAltYSettings({ scribeApiKey, scribeGroqApiKey, scribeModel, scribeLanguage });
      }
      // BYOK keys: server wins; local cache is just a placeholder while the
      // GET is in flight. Echo each loaded value back into localStorage so the
      // inputs show the previous value on the next reload too.
      if (typeof server.openrouterApiKey === 'string') {
        setOpenrouterApiKey(server.openrouterApiKey);
        localStorage.setItem('altY.openrouterApiKey', server.openrouterApiKey);
      }
      if (typeof server.openaiApiKey === 'string') {
        setOpenaiApiKey(server.openaiApiKey);
        localStorage.setItem('altY.openaiApiKey', server.openaiApiKey);
      }
      if (serverAutoSubmitTipSeen) {
        localStorage.setItem('altY.autoSubmitTipSeen', '1');
        autoSubmitTipSeenRef.current = true;
        setAutoSubmitTipSeen(true);
        setAutoSubmitTip(false);
      } else if (localAutoSubmitTipSeen) {
        void updateAltYSettings({ autoSubmitTipSeen: true });
      }
      scribeServerLoadedRef.current = true;
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to the broker (debounced) after the initial load, so typing a key
  // or switching models syncs without a request per keystroke. On a successful
  // write, flip the key-input's save status from 'saving' (set by onChange) to
  // 'saved' so the badge confirms the persist. Model/language changes save the
  // same way but never set 'saving', so they don't trigger the badge.
  useEffect(() => {
    if (!scribeServerLoadedRef.current) return;
    const timer = window.setTimeout(() => {
      void updateAltYSettings({ scribeApiKey, scribeGroqApiKey, scribeModel, scribeLanguage }).then((result) => {
        if (!result || Object.keys(result).length === 0) return;
        if (scribeKeySaveStatusRef.current === 'saving') setScribeKeySaveStatus('saved');
      });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [scribeApiKey, scribeGroqApiKey, scribeModel, scribeLanguage]);

  // Same debounced persist pattern for the BYOK keys: 500ms after the last
  // keystroke, mirror into localStorage (for next reload) and push to the
  // broker, which re-emits them into `.mcp.json` on the next turn. Each field's
  // status flips independently — only the input the user actually typed in had
  // its status set to 'saving' by onChange, so the other stays 'idle'.
  useEffect(() => {
    if (!scribeServerLoadedRef.current) return;
    const timer = window.setTimeout(() => {
      localStorage.setItem('altY.openrouterApiKey', openrouterApiKey);
      localStorage.setItem('altY.openaiApiKey', openaiApiKey);
      void updateAltYSettings({ openrouterApiKey, openaiApiKey }).then((result) => {
        if (!result || Object.keys(result).length === 0) return;
        if (openrouterKeySaveStatusRef.current === 'saving') setOpenrouterKeySaveStatus('saved');
        if (openaiKeySaveStatusRef.current === 'saving') setOpenaiKeySaveStatus('saved');
      });
    }, 500);
    return () => window.clearTimeout(timer);
  }, [openrouterApiKey, openaiApiKey]);

  // Auto-clear each "Saved" badge back to idle after 2.5s so it's a transient
  // confirmation, not a permanent label.
  useEffect(() => {
    if (scribeKeySaveStatus !== 'saved') return;
    const t = window.setTimeout(() => setScribeKeySaveStatus('idle'), 2500);
    return () => window.clearTimeout(t);
  }, [scribeKeySaveStatus]);
  useEffect(() => {
    if (openrouterKeySaveStatus !== 'saved') return;
    const t = window.setTimeout(() => setOpenrouterKeySaveStatus('idle'), 2500);
    return () => window.clearTimeout(t);
  }, [openrouterKeySaveStatus]);
  useEffect(() => {
    if (openaiKeySaveStatus !== 'saved') return;
    const t = window.setTimeout(() => setOpenaiKeySaveStatus('idle'), 2500);
    return () => window.clearTimeout(t);
  }, [openaiKeySaveStatus]);

  // Drop the "add a key" emphasis once the settings popover is closed, so it
  // only pulses on the open that the mic click triggered.
  useEffect(() => {
    if (!scribeSettingsOpen) setScribeKeyNudge(false);
  }, [scribeSettingsOpen]);

  // Poll whether the broker is running stale code (a dev turn edited it) and
  // whether a newer app bundle has been deployed. When either is true the ⚙ cog
  // + matching cog-menu button go amber. Polls on mount, on a 20s interval, and
  // whenever the settings popover is opened (so the flags are fresh the moment
  // the user looks). The restart flag self-clears after the restart; the update
  // flag clears once the page reloads onto the new bundle.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const [status, deployedBuildId] = await Promise.all([getBrokerStatus(), getDeployedAppBuildId()]);
      if (cancelled) return;
      setBrokerRestartNeeded(status.restartNeeded);
      setAppUpdateAvailable(!!deployedBuildId && deployedBuildId !== RUNNING_BUILD_ID);
    };
    void check();
    const timer = window.setInterval(check, 20000);
    return () => { cancelled = true; window.clearInterval(timer); };
  }, []);
  useEffect(() => {
    if (!scribeSettingsOpen) return;
    void getBrokerStatus().then((s) => setBrokerRestartNeeded(s.restartNeeded));
    void getDeployedAppBuildId().then((id) => setAppUpdateAvailable(!!id && id !== RUNNING_BUILD_ID));
  }, [scribeSettingsOpen]);

  // One button covers both kinds of "update ready": a fresh bundle (front-end)
  // and stale broker code (back-end). If the broker needs to restart, do that
  // first so the reloaded page connects to the new server; then reload the
  // tab to pick up the new bundle. On testing/internal/online the broker-stale
  // flag is always false (its endpoint isn't reachable same-origin), so this
  // collapses to a plain reload there — which is what those hosts want.
  const handleReloadAltY = useCallback(() => {
    if (brokerRestarting) return;
    void (async () => {
      if (brokerRestartNeeded) {
        setBrokerRestarting(true);
        try {
          await restartBroker();
          await waitForBrokerReady();
        } catch {
          // Reload anyway — worst case the user sees the amber state again.
        }
      }
      // The bundle filenames are stable (assets/index.{js,css}) so a plain
      // reload may serve the cached old build. Force-revalidate the assets
      // with `cache: 'reload'` before navigating — the asset cache is then
      // up-to-date for the reloaded page. Errors are best-effort: even if
      // the prefetch fails, the reload still happens.
      try {
        const hrefs = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"][href]'))
          .map((el) => el.href)
          .filter((url) => url.includes('/alty-app/') || url.endsWith('/index.css'));
        const srcs = Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]'))
          .map((el) => el.src)
          .filter((url) => url.includes('/alty-app/') || url.endsWith('/index.js'));
        await Promise.all([...hrefs, ...srcs].map((url) =>
          fetch(url, { cache: 'reload', credentials: 'include' }).catch(() => null),
        ));
      } catch {
        // ignore
      }
      window.location.reload();
    })();
  }, [brokerRestartNeeded, brokerRestarting]);

  const closeChatDetails = useCallback(() => {
    chatDetailsRequestRef.current += 1;
    setChatDetails(null);
    setTitleEditing(false);
    setTitleError('');
  }, []);

  const showChatDetails = useCallback((summary: AltYChatSummary) => {
    const requestId = chatDetailsRequestRef.current + 1;
    const title = summary.title?.trim() || `Chat ${summary.runId}`;
    chatDetailsRequestRef.current = requestId;
    setTitleEditing(false);
    setTitleDraft(title);
    setTitleError('');
    setChatDetails({
      chat: summary,
      originalQuestion: '',
      loading: true,
      error: '',
    });
    void getAltYTranscript(summary.runId).then((transcript) => {
      if (chatDetailsRequestRef.current !== requestId) return;
      const originalQuestion = firstUserQuestion(Array.isArray(transcript.events) ? transcript.events : []);
      setChatDetails((current) => {
        if (!current || current.chat.runId !== summary.runId) return current;
        return {
          ...current,
          originalQuestion,
          loading: false,
          error: originalQuestion ? '' : 'No original question was saved for this chat yet.',
        };
      });
    }).catch((error) => {
      if (chatDetailsRequestRef.current !== requestId) return;
      setChatDetails((current) => {
        if (!current || current.chat.runId !== summary.runId) return current;
        return {
          ...current,
          loading: false,
          error: (error as Error).message || 'Could not load this chat transcript.',
        };
      });
    });
  }, []);

  const startTitleEdit = useCallback(() => {
    if (!chatDetails) return;
    setTitleDraft(chatDetails.chat.title?.trim() || `Chat ${chatDetails.chat.runId}`);
    setTitleError('');
    setTitleEditing(true);
  }, [chatDetails]);

  const cancelTitleEdit = useCallback(() => {
    if (!chatDetails) return;
    setTitleDraft(chatDetails.chat.title?.trim() || `Chat ${chatDetails.chat.runId}`);
    setTitleError('');
    setTitleEditing(false);
  }, [chatDetails]);

  const saveTitleEdit = useCallback(async () => {
    if (!chatDetails || titleSaving) return;
    const trimmed = titleDraft.trim();
    if (!trimmed) {
      setTitleError('Enter a title before saving.');
      return;
    }
    const nextTitle = trimmed.slice(0, 80);
    const currentTitle = chatDetails.chat.title?.trim() || `Chat ${chatDetails.chat.runId}`;
    if (nextTitle === currentTitle) {
      setTitleEditing(false);
      setTitleError('');
      return;
    }
    setTitleSaving(true);
    setTitleError('');
    try {
      await chat.renameChat(chatDetails.chat.runId, nextTitle);
      setChatDetails((current) => {
        if (!current || current.chat.runId !== chatDetails.chat.runId) return current;
        return { ...current, chat: { ...current.chat, title: nextTitle } };
      });
      setTitleDraft(nextTitle);
      setTitleEditing(false);
    } catch (error) {
      setTitleError((error as Error).message || 'Could not rename this chat.');
    } finally {
      setTitleSaving(false);
    }
  }, [chat, chatDetails, titleDraft, titleSaving]);

  // Dismiss the speech-to-text settings popover on outside click or Escape.
  useEffect(() => {
    if (!scribeSettingsOpen) return;
    const onDown = (event: MouseEvent) => {
      if (!scribeSettingsRef.current?.contains(event.target as Node)) setScribeSettingsOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setScribeSettingsOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [scribeSettingsOpen]);

  useEffect(() => {
    if (!chatDetails) return;
    const onDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (chatInfoRef.current?.contains(target)) return;
      if (activeChatTitleRef.current?.contains(target)) return;
      closeChatDetails();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeChatDetails();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [chatDetails, closeChatDetails]);

  useEffect(() => {
    if (!titleEditing) return;
    titleInputRef.current?.focus();
    titleInputRef.current?.select();
  }, [titleEditing]);

  function startResize(event: React.MouseEvent) {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = width;
    let nextWidth = width;

    // While dragging, stop the OneSheet iframe from swallowing mouse events:
    // once the cursor crosses into it the window stops getting mousemove and
    // the drag would freeze.
    if (appFrame) appFrame.style.pointerEvents = 'none';

    function move(moveEvent: MouseEvent) {
      nextWidth = clampWidth(startWidth + (startX - moveEvent.clientX));
      setWidth(nextWidth);
    }

    function up() {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
      if (appFrame) appFrame.style.pointerEvents = '';
      localStorage.setItem(sizeKey, JSON.stringify({ width: nextWidth }));
    }

    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  }

  // Thinking-level options for the current model (empty for gemini/cerebras).
  // The stored `thinking` value is clamped to what this model offers, so a
  // claude "max" gracefully becomes codex's top level ("xhigh") rather than an
  // invalid selection — used for both the dropdown value and the sent payload.
  const thinkingOptions = THINKING_BY_MODEL[model];
  const supportsThinking = thinkingOptions.length > 0;
  const supportsFast = FAST_MODELS.has(model);
  const supportsMax = MAX_MODELS.has(model);
  const showComposerFastToggle = model === 'cursor';
  const supportsFastInSettings = supportsFast && !showComposerFastToggle;
  // "Exit Alt-Y": leave the /AltY shell and return to the host's base domain
  // (e.g. https://ai.idsys.ee/AltY → https://ai.idsys.ee/). Only meaningful when
  // embedded under a sub-path; the standalone broker serves the app at the root
  // so there's nothing to exit to and we hide the action there.
  const exitToBaseUrl = window.location.origin + '/';
  const canExitToBase = window.location.pathname.replace(/\/+$/, '') !== '';
  const effectiveThinking = supportsThinking
    ? (thinkingOptions.includes(thinking) ? thinking : thinkingOptions[thinkingOptions.length - 1])
    : thinking;
  // The broker still routes on a single `effort` token: claude carries its
  // thinking level there; the alternate models carry their routing token.
  const routingEffort =
    model === 'gemini' ? 'gemini-3.5'
      : model === 'cerebras' ? 'cerebras'
        : model === 'codex' ? 'codex'
          : model === 'cursor' ? 'cursor'
            : model === 'grok' ? 'grok'
              : effectiveThinking;

  // The broker compiles summaries after each finished turn. Surface the latest
  // turn and whole-chat versions as an info block while idle; during a turn the
  // thinking footer is the live progress, and summaries still reflect the
  // previous completed turn.
  const activeChat = chat.chats.find((item) => item.runId === chat.activeRunId);
  const activeChatId = activeChat?.runId ?? chat.activeRunId ?? null;
  const activeChatTitle = activeChat?.title?.trim() || (activeChatId == null ? 'New chat' : `Chat ${activeChatId}`);
  const activeOverallSummary = activeChat?.overallSummary?.trim() || activeChat?.subtitle?.trim() || '';
  const activeLastTurnSummary = activeChat?.lastTurnSummary?.trim() || '';
  const activeChatTooltip = [
    activeLastTurnSummary ? `Last turn: ${activeLastTurnSummary}` : '',
    activeOverallSummary && activeOverallSummary !== activeLastTurnSummary ? `Overall: ${activeOverallSummary}` : '',
  ].filter(Boolean).join(' · ') || activeChatTitle;
  const showDoneSummary = !chat.busy && !!(activeLastTurnSummary || activeOverallSummary) && chat.messages.length > 0;
  const canStartSubchat = !!activeChat;
  const newChatLabel = chat.mode === 'agent-factory' ? 'New agent' : 'New chat';
  const newChatTitle = newChatLabel;
  const newSubchatLabel = 'New subchat';
  const newSubchatTitle = `New subchat under ${activeChat?.title || `Chat ${activeChat?.runId || ''}`}`.trim();

  // The chat rows to render in the sidebar, after applying the search box.
  // No query → the full list, untouched. With a query: use the Cerebras
  // ranking when one has landed for THIS exact query (matches only, best
  // first); otherwise fall back to an instant case-insensitive substring
  // filter over the title + summary so typing always feels responsive and the
  // box still works if the intelligent search is unavailable.
  //
  // Memoised on the inputs that actually change so streaming-message ticks (the
  // panel re-renders on every assistant text chunk) don't redo the filter, the
  // tree arrangement, and the title Map for the unchanged sidebar.
  const trimmedSearch = chatSearch.trim();
  const visibleChats = useMemo(() => {
    if (!trimmedSearch) return chat.chats;
    if (searchMatches && searchMatches.query === trimmedSearch) {
      const rank = new Map(searchMatches.order.map((runId, i) => [runId, i] as const));
      return chat.chats
        .filter((c) => rank.has(c.runId))
        .sort((a, b) => (rank.get(a.runId) ?? 0) - (rank.get(b.runId) ?? 0));
    }
    const needle = trimmedSearch.toLowerCase();
    return chat.chats.filter(
      (c) =>
        String(c.runId).includes(needle) ||
        `chat ${c.runId}`.includes(needle) ||
        `id ${c.runId}`.includes(needle) ||
        `run ${c.runId}`.includes(needle) ||
        `#${c.runId}`.includes(needle) ||
        `alty-${c.runId}`.includes(needle) ||
        (c.title || '').toLowerCase().includes(needle) ||
        (c.subtitle || '').toLowerCase().includes(needle) ||
        (c.overallSummary || '').toLowerCase().includes(needle) ||
        (c.lastTurnSummary || '').toLowerCase().includes(needle),
    );
  }, [chat.chats, trimmedSearch, searchMatches]);
  const visibleChatRows = useMemo(() => arrangeChatTree(visibleChats), [visibleChats]);
  const chatTitleById = useMemo(
    () => new Map(chat.chats.map((item) => [item.runId, item.title || `Chat ${item.runId}`] as const)),
    [chat.chats],
  );

  // Split the visible chat rows into per-group sections + the ungrouped tail.
  // Searching collapses everything into the flat ungrouped list so matches
  // aren't hidden under a collapsed folder — folders are an organisation aid,
  // not a way to lose results.
  const groupedSections = useMemo(() => {
    if (trimmedSearch) return [] as Array<{ group: AltYChatGroup; rows: AltYChatSummary[] }>;
    const groups = chat.chatGroups || [];
    if (!groups.length) return [];
    const byId = new Map<number, AltYChatSummary[]>();
    for (const row of visibleChatRows) {
      const gid = row.groupId ?? null;
      if (gid == null) continue;
      const list = byId.get(gid) || [];
      list.push(row);
      byId.set(gid, list);
    }
    return groups.map((group) => ({ group, rows: byId.get(group.id) || [] }));
  }, [chat.chatGroups, visibleChatRows, trimmedSearch]);

  const ungroupedRows = useMemo(() => {
    if (trimmedSearch) return visibleChatRows;
    const groupIds = new Set((chat.chatGroups || []).map((g) => g.id));
    return visibleChatRows.filter((row) => {
      const gid = row.groupId;
      // A chat whose stored group was deleted but not yet refreshed locally is
      // treated as ungrouped so it doesn't vanish from the sidebar.
      return gid == null || !groupIds.has(gid);
    });
  }, [chat.chatGroups, visibleChatRows, trimmedSearch]);
  const modelComboValue = supportsThinking ? `${model}:${effectiveThinking}` : model;
  const detailsChat = chatDetails ? (chat.chats.find((item) => item.runId === chatDetails.chat.runId) || chatDetails.chat) : undefined;
  const chatDetailsTitle = detailsChat?.title?.trim() || (detailsChat ? `Chat ${detailsChat.runId}` : '');
  const chatDetailsOverallSummary = detailsChat?.overallSummary?.trim() || detailsChat?.subtitle?.trim() || '';
  const chatDetailsLastTurnSummary = detailsChat?.lastTurnSummary?.trim() || '';
  const chatDetailsMode = detailsChat ? (MODE_BADGE_LABEL[detailsChat.mode] || detailsChat.mode) : '';
  const chatDetailsAge = detailsChat ? formatAgo(detailsChat.lastActivity) : '';
  const showChatInfo = !!chatDetails && !!detailsChat && detailsChat.runId === activeChatId;
  const handleActiveChatTitleClick = useCallback(() => {
    if (!activeChat) return;
    if (showChatInfo) {
      closeChatDetails();
      return;
    }
    showChatDetails(activeChat);
  }, [activeChat, closeChatDetails, showChatDetails, showChatInfo]);

  useEffect(() => {
    if (chatDetails && activeChatId !== chatDetails.chat.runId) closeChatDetails();
  }, [activeChatId, chatDetails, closeChatDetails]);

  // Compose the per-turn consensus list — the primary picker is always first,
  // followed by every `extraModels` entry (deduped). Each entry carries enough
  // for the broker to dispatch it: `effort` is the routing token (claude's
  // thinking level, or the model id for the other providers), `codexEffort` is
  // codex's thinking level, `fast` is the fast-mode flag. Returns null when
  // there's only one model picked → caller stays on the single-model path.
  function buildConsensusModels(): ConsensusModel[] | null {
    if (extraModels.length === 0) return null;
    const primary = supportsThinking ? `${model}:${effectiveThinking}` : model;
    const ordered = [primary, ...extraModels.filter((v) => v !== primary)];
    if (ordered.length < 2) return null;
    const out: ConsensusModel[] = [];
    for (const value of ordered) {
      const parsed = parseModelOption(value);
      if (!parsed) continue;
      const supportsT = THINKING_BY_MODEL[parsed.model].length > 0;
      const thinkingForEntry = parsed.thinking || (supportsT ? THINKING_BY_MODEL[parsed.model].slice(-1)[0] : undefined);
      const routing =
        parsed.model === 'gemini' ? 'gemini-3.5'
          : parsed.model === 'cerebras' ? 'cerebras'
            : parsed.model === 'codex' ? 'codex'
              : parsed.model === 'cursor' ? 'cursor'
                : parsed.model === 'grok' ? 'grok'
                  : (thinkingForEntry || 'high');
      const entry: ConsensusModel = {
        effort: routing,
        fast: FAST_MODELS.has(parsed.model) && fast,
        max: MAX_MODELS.has(parsed.model) && maxMode,
        label: labelForModelOption(value),
      };
      if (parsed.model === 'codex' && thinkingForEntry) entry.codexEffort = thinkingForEntry;
      out.push(entry);
    }
    return out.length >= 2 ? out : null;
  }

  function send() {
    const text = draft.trim();
    if (!text && pendingImages.length === 0) return;
    // Mark this as a genuine send so the per-chat memory effect records the
    // current agent + model against the chat this message lands in.
    justSentRef.current = true;
    if (userIds.length > 0) {
      // Broadcast: the broker accepts one recipient per call, so fan out across
      // selections. Each adds the recipient as a chat participant on the broker
      // side and emits its own _human event.
      for (const recipientId of userIds) {
        void chat.sendHumanMessage(text, recipientId, undefined, pendingImages);
      }
    } else {
      const consensus = buildConsensusModels();
      chat.sendMessage(text, routingEffort, pendingImages, {
        codexEffort: model === 'codex' ? effectiveThinking : undefined,
        fast: supportsFast && fast,
        max: supportsMax && maxMode,
        models: consensus || undefined,
      });
    }
    setDraft('');
    setPendingImages([]);
    // First message ever, auto-submit still on → surface the one-time nudge so
    // the user discovers it and where to turn it off.
    if (autoSubmit && !autoSubmitTipSeenRef.current) {
      localStorage.setItem('altY.autoSubmitTipSeen', '1');
      autoSubmitTipSeenRef.current = true;
      setAutoSubmitTipSeen(true);
      if (scribeServerLoadedRef.current) {
        void updateAltYSettings({ autoSubmitTipSeen: true });
      }
      setAutoSubmitTip(true);
    }
  }

  // Resend a prior prompt verbatim (the message's per-row "rerun" button) using
  // the current model/effort settings. Held in a ref so the callback handed to
  // every memoized <Message> stays referentially stable across the per-frame
  // streaming re-renders — otherwise the whole transcript re-renders each tick.
  rerunMessageRef.current = (message: AltYChatMessage) => {
    if (!message.text.trim() && !message.images?.length) return;
    justSentRef.current = true;
    if (userIds.length > 0) {
      for (const recipientId of userIds) {
        void chat.sendHumanMessage(message.text, recipientId, undefined, message.images || []);
      }
    } else {
      const consensus = buildConsensusModels();
      chat.sendMessage(message.text, routingEffort, message.images || [], {
        codexEffort: model === 'codex' ? effectiveThinking : undefined,
        fast: supportsFast && fast,
        max: supportsMax && maxMode,
        models: consensus || undefined,
      });
    }
  };

  async function addFiles(files: File[]) {
    const attachments: AltYImageAttachment[] = [];
    for (const file of files) {
      try {
        const attachment = await fileToAttachment(file);
        attachments.push(attachment);
      } catch {
        /* skip a file we could not read */
      }
    }
    if (attachments.length) {
      setPendingImages((list) => appendUniqueAttachments(list, attachments));
    }
  }

  function handlePaste(event: React.ClipboardEvent<HTMLTextAreaElement>) {
    const files = imagesFromDataTransfer(event.clipboardData);
    if (!files.length) return;
    event.preventDefault();
    void addFiles(files);
  }

  function handleDrop(event: React.DragEvent<HTMLElement>) {
    const files = imagesFromDataTransfer(event.dataTransfer);
    if (!files.length) return;
    event.preventDefault();
    void addFiles(files);
  }

  // The attach button (the only way to attach on mobile, where paste/drag-drop
  // aren't available) opens the native file picker — camera, photo library and
  // file browser on a phone. Reset the value so re-picking the same file fires.
  function handleAttachInput(event: React.ChangeEvent<HTMLInputElement>) {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length) void addFiles(files);
    event.target.value = '';
  }

  return (
    <aside
      ref={panelRef}
      className={`alty-react-panel${isOpen ? ' open' : ''}${mobile ? ' mobile' : ''}`}
      style={
        mobile
          ? ({
              // Pin the panel to the visible region above the keyboard. When
              // the visualViewport API is unavailable `--alty-vvh` is unset and
              // the CSS falls back to 100dvh; offsetTop is non-zero on iOS when
              // the page is scrolled under the keyboard.
              ...(viewport.height != null
                ? { '--alty-vvh': `${viewport.height}px` }
                : {}),
              transform: viewport.offsetTop ? `translateY(${viewport.offsetTop}px)` : undefined,
            } as React.CSSProperties)
          : { width: clampWidth(width) }
      }
      aria-hidden={!isOpen}
    >
      {mobile ? null : <div className="alty-resize-handle" onMouseDown={startResize} />}
      {mobile && drawerOpen ? (
        <div className="alty-drawer-backdrop" onClick={closeDrawer} aria-hidden="true" />
      ) : null}
      <div
        className={[
          'alty-sidebar',
          chat.sidebarExpanded ? 'expanded' : '',
          mobile ? 'drawer' : '',
          mobile && drawerOpen ? 'open' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="alty-sidebar-head">
          {mobile ? (
            <button type="button" className="alty-icon-button alty-drawer-close" title="Close chats" aria-label="Close chats" onClick={closeDrawer}>
              <i className="fas fa-times" aria-hidden="true" />
            </button>
          ) : (
            <button type="button" className="alty-icon-button" title="Toggle chats" onClick={() => chat.setSidebarExpanded(!chat.sidebarExpanded)}>
              <i className="fas fa-bars" aria-hidden="true" />
            </button>
          )}
          {chat.sidebarExpanded || mobile ? <span className="alty-sidebar-head-label">Chats</span> : null}
          <div className="alty-env-tabs" hidden={mobile}>
            {mobile ? null : tabs.map((tab) => {
              const active = tab.id === activeTabId;
              return (
                <button
                  key={tab.id}
                  type="button"
                  className={active ? 'alty-env-tab active' : 'alty-env-tab'}
                  onClick={() => onSelectTab(tab)}
                  title={tab.label}
                  aria-label={tab.label}
                  aria-pressed={active}
                >
                  {tab.icon === 'claude' ? <ClaudeIcon /> : <i className={`fas ${tab.icon || 'fa-circle'}`} aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>
        {mobile ? null : (
          <ParticipantsPicker
            agents={pinnedAgents}
            selectedAgentId={selectedAgentId}
            selectedUserIds={userIds}
            expanded={expandedView}
            onSelectAgent={(id) => onSelectPinnedAgent(id)}
            onSelectUsers={(ids) => onSelectUserIds?.(ids)}
            onEditAgent={handleEditPinnedAgent}
            onDeleteAgent={onDeletePinnedAgent}
          />
        )}
        <div className="alty-pin-list">
          {pinnedAgents.filter((agent) => agent.pinned !== false).map((agent) => {
            const deletable = agent.id.startsWith('user-');
            const editable = deletable || EDITABLE_BUILTIN_PIN_IDS.has(agent.id);
            const active = agent.id === selectedPinnedAgentId;
            return (
              <button
                key={agent.id}
                type="button"
                className={active ? 'alty-agent-row active' : 'alty-agent-row'}
                onClick={() => onSelectPinnedAgent(agent.id)}
                title={agent.label}
              >
                <i className={`fas ${agent.icon}`} aria-hidden="true" />
                {expandedView ? <span className="alty-agent-label">{agent.label}</span> : null}
                {expandedView ? (
                  <span className="alty-pinned-pill" aria-hidden="true">
                    <i className="fas fa-thumbtack" />
                  </span>
                ) : null}
                {expandedView && editable ? (
                  <span
                    role="button"
                    tabIndex={0}
                    className="alty-pin-edit"
                    title="Edit pinned agent"
                    aria-label={`Edit ${agent.label}`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      handleEditPinnedAgent(agent.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        handleEditPinnedAgent(agent.id);
                      }
                    }}
                  >
                    <i className="fas fa-pen" aria-hidden="true" />
                  </span>
                ) : null}
                {expandedView && deletable ? (
                  <span
                    role="button"
                    tabIndex={0}
                    className="alty-chat-delete"
                    title="Delete pinned agent"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      onDeletePinnedAgent(agent.id);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        onDeletePinnedAgent(agent.id);
                      }
                    }}
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
        {/* Mobile already has a "+" new-chat icon in the top bar, so the
            sidebar's New-chat entry is redundant there — desktop/tablet keep it.
            "New group" sits above New chat: it creates a sidebar folder the
            user can drag chats into. */}
        {mobile ? null : (
          <div className="alty-new-chat-actions">
            {expandedView ? (
              <button
                type="button"
                className="alty-new-chat alty-new-group"
                title="New group (folder for organising chats)"
                onClick={handleCreateGroup}
              >
                <i className="fas fa-folder-plus" aria-hidden="true" />
                <span>New group</span>
              </button>
            ) : null}
            <button
              type="button"
              className="alty-new-chat"
              title={newChatTitle}
              onClick={() => { onStartNewChat(); setDrawerOpen(false); }}
            >
              <i className="fas fa-plus" aria-hidden="true" />
              {expandedView ? <span>{newChatLabel}</span> : null}
            </button>
            {canStartSubchat ? (
              <button
                type="button"
                className="alty-new-chat alty-new-subchat"
                title={newSubchatTitle}
                onClick={handleStartSubchat}
              >
                <i className="fas fa-code-branch" aria-hidden="true" />
                {expandedView ? <span>{newSubchatLabel}</span> : null}
              </button>
            ) : null}
          </div>
        )}
        {/* Search box — intelligent (Cerebras-ranked) chat filter. Only in the
            expanded sidebar / mobile drawer; the slim icon-rail has no room. */}
        {expandedView ? (
          <div className="alty-chat-search">
            <i className={`fas ${searching ? 'fa-spinner fa-spin' : 'fa-search'}`} aria-hidden="true" />
            <input
              type="search"
              value={chatSearch}
              onChange={(event) => setChatSearch(event.target.value)}
              placeholder="Search chats or ID…"
              aria-label="Search chats"
            />
            {chatSearch ? (
              <button
                type="button"
                className="alty-chat-search-clear"
                title="Clear search"
                aria-label="Clear search"
                onClick={() => setChatSearch('')}
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            ) : null}
          </div>
        ) : null}
        <div className="alty-chat-list">
          {trimmedSearch && visibleChats.length === 0 ? (
            <div className="alty-chat-search-empty">
              {searching ? 'Searching…' : `No chats match “${trimmedSearch}”.`}
            </div>
          ) : (
            <>
              {expandedView ? groupedSections.map(({ group, rows }) => (
                <ChatGroupSection
                  key={`group-${group.id}`}
                  group={group}
                  rows={rows}
                  collapsed={collapsedGroupIds.has(group.id)}
                  editing={editingGroupId === group.id}
                  dragOver={dragOverGroupId === group.id}
                  onToggleCollapsed={toggleGroupCollapsed}
                  onStartRename={(id) => setEditingGroupId(id)}
                  onCancelRename={() => setEditingGroupId(null)}
                  onCommitRename={handleGroupRename}
                  onDelete={handleGroupDelete}
                  onNewChat={handleGroupNewChat}
                  onDragEnter={(id) => setDragOverGroupId(id)}
                  onDragLeave={(id) => setDragOverGroupId((prev) => (prev === id ? null : prev))}
                  onDrop={(id, event) => acceptChatDrop(id, event)}
                >
                  {rows.map((item) => (
                    <ChatRow
                      key={item.runId}
                      chat={item}
                      active={item.runId === chat.activeRunId}
                      expanded={expandedView}
                      parentTitle={typeof item.parentRunId === 'number' ? chatTitleById.get(item.parentRunId) : null}
                      onSelect={handleChatSelect}
                      onStop={handleChatStop}
                      onDelete={handleChatDelete}
                      onDragStart={handleChatDragStart}
                      onDragEnd={handleChatDragEnd}
                      dragging={draggingChatRunId === item.runId}
                    />
                  ))}
                </ChatGroupSection>
              )) : null}
              {/* Ungrouped tail — also a drop target so a chat can be dragged
                  back out of a folder. The class flips when a chat is being
                  dragged so the empty area is discoverable as a target. */}
              <div
                className={[
                  'alty-chat-ungrouped',
                  draggingChatRunId != null ? 'drag-active' : '',
                  dragOverGroupId === -1 ? 'drop-target' : '',
                ].filter(Boolean).join(' ')}
                onDragOver={(event) => {
                  if (draggingChatRunId == null) return;
                  event.preventDefault();
                  event.dataTransfer.dropEffect = 'move';
                }}
                onDragEnter={() => { if (draggingChatRunId != null) setDragOverGroupId(-1); }}
                onDragLeave={(event) => {
                  if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
                  setDragOverGroupId((prev) => (prev === -1 ? null : prev));
                }}
                onDrop={(event) => acceptChatDrop(null, event)}
              >
                {expandedView && groupedSections.length > 0 && draggingChatRunId != null && ungroupedRows.length === 0 ? (
                  <div className="alty-chat-group-empty alty-chat-ungrouped-hint">Drop here to remove from group</div>
                ) : null}
                {ungroupedRows.map((item) => (
                  <ChatRow
                    key={item.runId}
                    chat={item}
                    active={item.runId === chat.activeRunId}
                    expanded={expandedView}
                    parentTitle={typeof item.parentRunId === 'number' ? chatTitleById.get(item.parentRunId) : null}
                    onSelect={handleChatSelect}
                    onStop={handleChatStop}
                    onDelete={handleChatDelete}
                    onDragStart={expandedView ? handleChatDragStart : undefined}
                    onDragEnd={expandedView ? handleChatDragEnd : undefined}
                    dragging={draggingChatRunId === item.runId}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <section className="alty-chat-panel">
        <header className="alty-chat-header">
          {mobile ? (
            <>
              <button
                type="button"
                className="alty-icon-button alty-hamburger"
                title="Chats"
                aria-label="Open chats"
                onClick={openDrawer}
              >
                <i className="fas fa-bars" aria-hidden="true" />
              </button>
              <button
                type="button"
                className="alty-icon-button alty-new-chat-header"
                title={newChatTitle}
                aria-label={newChatLabel}
                onClick={() => { onStartNewChat(); setDrawerOpen(false); }}
              >
                <i className="fas fa-plus" aria-hidden="true" />
              </button>
              {canStartSubchat ? (
                <button
                  type="button"
                  className="alty-icon-button alty-new-chat-header alty-new-subchat-header"
                  title={newSubchatTitle}
                  aria-label={newSubchatLabel}
                  onClick={handleStartSubchat}
                >
                  <i className="fas fa-code-branch" aria-hidden="true" />
                </button>
              ) : null}
            </>
          ) : null}

          {mobile ? (
            // Mobile: the active agent's icon sits just left of the combined
            // model+thinking dropdown. Tapping the icon opens the same searchable
            // agent picker the drawer uses, so the agent can be switched right
            // here without opening the chats drawer.
            <div className="alty-header-agent-picker">
              <ParticipantsPicker
                agents={pinnedAgents}
                selectedAgentId={selectedAgentId}
                selectedUserIds={userIds}
                expanded={false}
                onSelectAgent={onSelectPinnedAgent}
                onSelectUsers={(ids) => onSelectUserIds?.(ids)}
                onEditAgent={handleEditPinnedAgent}
                onDeleteAgent={onDeletePinnedAgent}
              />
            </div>
          ) : null}
          <div className="alty-models-picker" ref={modelsPopoverRef}>
            <button
              type="button"
              className={`alty-model alty-model-combo alty-models-trigger${extraModels.length ? ' multi' : ''}`}
              data-tip={pricingTipDataAttr(
                extraModels.length
                  ? `Consensus across ${1 + extraModels.length} models — each runs in a subchat and the verdict is synthesised in the main chat`
                  : modelOptionPricingTooltip(modelComboValue, { fast, maxMode }),
              )}
              title={
                extraModels.length
                  ? `Consensus across ${1 + extraModels.length} models — each runs in a subchat and the verdict is synthesised in the main chat`
                  : modelOptionPricingTooltip(modelComboValue, { fast, maxMode })
              }
              aria-haspopup="dialog"
              aria-expanded={modelsPopoverOpen}
              onClick={() => setModelsPopoverOpen((open) => !open)}
            >
              {extraModels.length
                ? `${1 + extraModels.length} models`
                : labelForModelOption(modelComboValue)}
              <i className="fas fa-caret-down alty-models-caret" aria-hidden="true" />
            </button>
            {modelsPopoverOpen ? (
              <div className="alty-models-popover" role="dialog" aria-label="Pick models">
                <div className="alty-models-popover-title">
                  Models
                  <span className="alty-models-popover-hint">
                    Pick more than one for a consensus turn
                  </span>
                </div>
                {allModelOptionValues().map((value) => {
                  const isPrimary = value === modelComboValue;
                  const isExtra = extraModels.includes(value);
                  const checked = isPrimary || isExtra;
                  const optionTip = modelOptionPricingTooltip(
                    value,
                    isPrimary ? { fast, maxMode } : undefined,
                  );
                  return (
                    <label
                      key={value}
                      className={`alty-models-option${checked ? ' on' : ''}${isPrimary ? ' primary' : ''}`}
                      data-tip={pricingTipDataAttr(optionTip)}
                      title={optionTip}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(event) => {
                          const next = event.target.checked;
                          if (next) {
                            // Newly checked: if nothing was selected at all, this
                            // becomes the primary; otherwise it joins the extras.
                            if (isExtra) return;
                            const parsed = parseModelOption(value);
                            if (!parsed) return;
                            setExtraModels((list) =>
                              list.includes(value) ? list : [...list, value],
                            );
                          } else {
                            if (isExtra) {
                              setExtraModels((list) => list.filter((v) => v !== value));
                              return;
                            }
                            if (isPrimary) {
                              // Un-checking the primary: promote the first extra
                              // (if any) to primary; otherwise refuse — at least
                              // one model must always be picked.
                              if (extraModels.length === 0) return;
                              const [nextPrimary, ...rest] = extraModels;
                              const parsed = parseModelOption(nextPrimary);
                              if (!parsed) return;
                              setModel(parsed.model);
                              if (parsed.thinking) setThinking(parsed.thinking);
                              setExtraModels(rest);
                            }
                          }
                        }}
                      />
                      <span className="alty-models-option-label">{labelForModelOption(value)}</span>
                      {isPrimary ? <span className="alty-models-option-tag">primary</span> : null}
                    </label>
                  );
                })}
                {extraModels.length ? (
                  <div className="alty-models-popover-foot">
                    {1 + extraModels.length} models → one subchat per model, main chat shows the verdict
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
          {showComposerFastToggle ? (
            <label
              className={`alty-fast${fast ? ' on' : ''}`}
              data-tip={cursorFastModeTooltip().replace(/\n/g, ' · ')}
              title={cursorFastModeTooltip()}
            >
              <input
                type="checkbox"
                checked={fast}
                onChange={(event) => setFast(event.target.checked)}
              />
              <i className="fas fa-bolt" aria-hidden="true" />
              Fast
            </label>
          ) : null}
          {!mobile ? (
            <div className="alty-active-chat-title-wrap">
              <button
                ref={activeChatTitleRef}
                type="button"
                className="alty-active-chat-title"
                title={activeChatTooltip}
                aria-label={`Show details for ${activeChatTitle}`}
                aria-haspopup="dialog"
                aria-expanded={showChatInfo}
                disabled={!activeChat}
                onClick={handleActiveChatTitleClick}
              >
                {activeChatTitle}
              </button>
              {activeChat && (
                <button
                  type="button"
                  className={showParticipants ? "alty-show-app active" : "alty-show-app"}
                  style={{ marginLeft: '8px', border: 'none', background: 'transparent' }}
                  onClick={() => setShowParticipants(!showParticipants)}
                  title="Manage participants"
                >
                  <i className="fas fa-users" aria-hidden="true" />
                  <span style={{ marginLeft: '4px', fontSize: '12px' }}>{chat.participants?.length || 1}</span>
                </button>
              )}
              {showChatInfo && detailsChat ? (
                <section
                  ref={chatInfoRef}
                  className="alty-chat-info-popover"
                  role="dialog"
                  aria-labelledby="alty-chat-info-title"
                  onMouseDown={(event) => event.stopPropagation()}
                >
                  <header className="alty-chat-info-head">
                    <div className="alty-chat-info-kicker">
                      <i className="fas fa-comment-dots" aria-hidden="true" />
                      Chat snapshot
                    </div>
                    <button
                      type="button"
                      className="alty-icon-button alty-chat-info-close"
                      title="Close"
                      aria-label="Close chat details"
                      onClick={closeChatDetails}
                    >
                      <i className="fas fa-times" aria-hidden="true" />
                    </button>
                  </header>
                  <div className="alty-chat-info-title-row" id="alty-chat-info-title">
                    {titleEditing ? (
                      <input
                        ref={titleInputRef}
                        className="alty-chat-info-title-input"
                        value={titleDraft}
                        maxLength={80}
                        aria-label="Chat title"
                        disabled={titleSaving}
                        onChange={(event) => {
                          setTitleDraft(event.target.value);
                          if (titleError) setTitleError('');
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            event.stopPropagation();
                            void saveTitleEdit();
                          } else if (event.key === 'Escape') {
                            event.preventDefault();
                            event.stopPropagation();
                            cancelTitleEdit();
                          }
                        }}
                      />
                    ) : (
                      <h2>{chatDetailsTitle}</h2>
                    )}
                    {titleEditing ? (
                      <span className="alty-chat-info-title-actions">
                        <button
                          type="button"
                          className="alty-icon-button alty-chat-title-action"
                          title="Save title"
                          aria-label="Save title"
                          disabled={titleSaving}
                          onClick={() => void saveTitleEdit()}
                        >
                          <i className={titleSaving ? 'fas fa-spinner fa-spin' : 'fas fa-check'} aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="alty-icon-button alty-chat-title-action"
                          title="Cancel title edit"
                          aria-label="Cancel title edit"
                          disabled={titleSaving}
                          onClick={cancelTitleEdit}
                        >
                          <i className="fas fa-times" aria-hidden="true" />
                        </button>
                      </span>
                    ) : (
                      <button
                        type="button"
                        className="alty-icon-button alty-chat-title-action"
                        title="Edit title"
                        aria-label="Edit title"
                        onClick={startTitleEdit}
                      >
                        <i className="fas fa-pen" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                  {titleError ? <div className="alty-chat-title-error">{titleError}</div> : null}
                  <div className="alty-chat-info-meta">
                    <span><i className="fas fa-hashtag" aria-hidden="true" /> {detailsChat.runId}</span>
                    <span><i className={`fas ${MODE_BADGE_ICON[detailsChat.mode] || 'fa-comments'}`} aria-hidden="true" /> {chatDetailsMode}</span>
                    {chatDetailsAge ? <span><i className="fas fa-clock" aria-hidden="true" /> {chatDetailsAge} ago</span> : null}
                  </div>
                  <div className="alty-chat-info-section primary">
                    <div className="alty-chat-info-label">
                      <i className="fas fa-quote-left" aria-hidden="true" />
                      Original question
                    </div>
                    {chatDetails.loading ? (
                      <div className="alty-chat-info-loading">Loading the first message…</div>
                    ) : chatDetails.error ? (
                      <p className="alty-chat-info-muted">{chatDetails.error}</p>
                    ) : (
                      <p>{chatDetails.originalQuestion}</p>
                    )}
                  </div>
                  {chatDetailsLastTurnSummary ? (
                    <div className="alty-chat-info-section">
                      <div className="alty-chat-info-label">
                        <i className="fas fa-reply" aria-hidden="true" />
                        Last turn summary
                      </div>
                      <p>{chatDetailsLastTurnSummary}</p>
                    </div>
                  ) : null}
                  {chatDetailsOverallSummary ? (
                    <div className="alty-chat-info-section">
                      <div className="alty-chat-info-label">
                        <i className="fas fa-magic" aria-hidden="true" />
                        Overall summary
                      </div>
                      <p>{chatDetailsOverallSummary}</p>
                    </div>
                  ) : null}
                </section>
              ) : null}
            </div>
          ) : null}
          <span className="alty-spacer" />
          {/* Save learnings / Deep-improve / Copy transcript used to be bare,
              label-less header icons on desktop. They now live in the ⚙ cog
              menu on both desktop and mobile (with labels + descriptions), so
              the header stays uncluttered and the actions read clearly. */}
          {mobile && canReopenDiagram && onReopenDiagram ? (
            // In agent-factory mode, once the user has closed the (full-screen)
            // diagram, this same header slot becomes the way back to it: instead
            // of the globe that opens the online app, show the diagram glyph that
            // reopens the Agent Factory diagram. Reuses the globe button's slot
            // and styling (.alty-show-app) — it's a true toggle for the diagram.
            <button
              type="button"
              className="alty-show-app"
              title="Reopen the diagram"
              aria-label="Reopen the diagram"
              onClick={onReopenDiagram}
            >
              <i className="fas fa-sitemap" aria-hidden="true" />
            </button>
          ) : mobile && canShowIdeas && onToggleIdeas ? (
            // Idea Agent chats: this slot becomes the ideas toggle instead of the
            // globe — flip between the conversation and the saved idea list. The
            // lightbulb opens the list; once open it shows the chat glyph to go
            // back. Same slot + styling as the globe it replaces.
            <button
              type="button"
              className="alty-show-app"
              title={ideasOpen ? 'Back to chat' : 'View your ideas'}
              aria-label={ideasOpen ? 'Back to chat' : 'View your ideas'}
              onClick={onToggleIdeas}
            >
              <i className={`fas ${ideasOpen ? 'fa-comments' : 'fa-lightbulb'}`} aria-hidden="true" />
            </button>
          ) : mobile && canShowApartments && onToggleApartments ? (
            // Apartment finder chats: this slot becomes the apartments toggle —
            // the buildings glyph opens the saved cards; once open it shows the
            // chat glyph to go back. Same slot + styling as the globe it replaces.
            <button
              type="button"
              className="alty-show-app"
              title={apartmentsOpen ? 'Back to chat' : 'View saved apartments'}
              aria-label={apartmentsOpen ? 'Back to chat' : 'View saved apartments'}
              onClick={onToggleApartments}
            >
              <i className={`fas ${apartmentsOpen ? 'fa-comments' : 'fa-building'}`} aria-hidden="true" />
            </button>
          ) : mobile && canShowBmadWorkflow && onToggleBmadWorkflow ? (
            // Customized BMad Agent Flow chats: this slot becomes the workflow
            // toggle — the clipboard glyph opens the workflow tabs; once open it
            // shows the chat glyph to go back. Same slot + styling as the globe.
            <button
              type="button"
              className="alty-show-app"
              title={bmadWorkflowOpen ? 'Back to chat' : 'View the BMad workflow'}
              aria-label={bmadWorkflowOpen ? 'Back to chat' : 'View the BMad workflow'}
              onClick={onToggleBmadWorkflow}
            >
              <i className={`fas ${bmadWorkflowOpen ? 'fa-comments' : 'fa-clipboard-list'}`} aria-hidden="true" />
            </button>
          ) : mobile && canShowDiscovery && onToggleDiscovery ? (
            <button
              type="button"
              className="alty-show-app"
              title={discoveryOpen ? 'Back to chat' : 'View the discovery brief'}
              aria-label={discoveryOpen ? 'Back to chat' : 'View the discovery brief'}
              onClick={onToggleDiscovery}
            >
              <i className={`fas ${discoveryOpen ? 'fa-comments' : 'fa-bullseye'}`} aria-hidden="true" />
            </button>
          ) : mobile && onShowApp ? (
            // In an online-dev chat this jumps to that chat's own dev environment
            // (App re-opens the dev panel); elsewhere it shows the online app. The
            // globe matches the icon the online-dev chat carries in the list. An
            // icon-only square button sitting just left of the cog — the mirror of
            // the app view's "Chat" back button (icon-only on mobile too).
            <button
              type="button"
              className="alty-show-app"
              title={chat.mode === 'online-dev-mvp' ? 'View dev environment' : 'View online app'}
              aria-label={chat.mode === 'online-dev-mvp' ? 'View dev environment' : 'View online app'}
              onClick={onShowApp}
            >
              <i className="fas fa-globe" aria-hidden="true" />
            </button>
          ) : null}
          {/* Desktop: an ideas toggle in the header (no globe there). Flips the
              right pane between the OneSheet iframe and the saved idea list.
              Only shown when an Idea Agent is the active selection. */}
          {!mobile && canShowIdeas && onToggleIdeas ? (
            <button
              type="button"
              className={ideasOpen ? 'alty-show-app active' : 'alty-show-app'}
              title={ideasOpen ? 'Back to the app' : 'View your ideas'}
              aria-label={ideasOpen ? 'Back to the app' : 'View your ideas'}
              aria-pressed={ideasOpen}
              onClick={onToggleIdeas}
            >
              <i className={`fas ${ideasOpen ? 'fa-globe' : 'fa-lightbulb'}`} aria-hidden="true" />
            </button>
          ) : null}
          {/* Desktop: an apartments toggle (no globe there). Flips the right
              pane between the OneSheet iframe and the saved apartment cards.
              Only shown when the apartment finder is the active selection. */}
          {!mobile && canShowApartments && onToggleApartments ? (
            <button
              type="button"
              className={apartmentsOpen ? 'alty-show-app active' : 'alty-show-app'}
              title={apartmentsOpen ? 'Back to the app' : 'View saved apartments'}
              aria-label={apartmentsOpen ? 'Back to the app' : 'View saved apartments'}
              aria-pressed={apartmentsOpen}
              onClick={onToggleApartments}
            >
              <i className={`fas ${apartmentsOpen ? 'fa-globe' : 'fa-building'}`} aria-hidden="true" />
            </button>
          ) : null}
          {/* Desktop: a BMad workflow toggle (no globe there). Flips the right
              pane between the OneSheet iframe and the workflow tabs. Only shown
              when the Customized BMad Agent Flow is the active selection. */}
          {!mobile && canShowBmadWorkflow && onToggleBmadWorkflow ? (
            <button
              type="button"
              className={bmadWorkflowOpen ? 'alty-show-app active' : 'alty-show-app'}
              title={bmadWorkflowOpen ? 'Back to the app' : 'View the BMad workflow'}
              aria-label={bmadWorkflowOpen ? 'Back to the app' : 'View the BMad workflow'}
              aria-pressed={bmadWorkflowOpen}
              onClick={onToggleBmadWorkflow}
            >
              <i className={`fas ${bmadWorkflowOpen ? 'fa-globe' : 'fa-clipboard-list'}`} aria-hidden="true" />
            </button>
          ) : null}
          {/* Desktop: a discovery-brief toggle (no globe there). Flips the right
              pane between the OneSheet iframe and the discovery brief. */}
          {!mobile && canShowDiscovery && onToggleDiscovery ? (
            <button
              type="button"
              className={discoveryOpen ? 'alty-show-app active' : 'alty-show-app'}
              title={discoveryOpen ? 'Back to the app' : 'View the discovery brief'}
              aria-label={discoveryOpen ? 'Back to the app' : 'View the discovery brief'}
              aria-pressed={discoveryOpen}
              onClick={onToggleDiscovery}
            >
              <i className={`fas ${discoveryOpen ? 'fa-globe' : 'fa-bullseye'}`} aria-hidden="true" />
            </button>
          ) : null}
          {mobile && activeChat ? (
            <button
              type="button"
              className={showParticipants ? "alty-show-app active" : "alty-show-app"}
              style={{ marginRight: '8px', border: 'none', background: 'transparent' }}
              onClick={() => setShowParticipants(!showParticipants)}
              title="Manage participants"
            >
              <i className="fas fa-users" aria-hidden="true" />
              <span style={{ marginLeft: '4px', fontSize: '11px' }}>{chat.participants?.length || 1}</span>
            </button>
          ) : null}
          <div className="alty-cog-wrap" ref={scribeSettingsRef}>
            <button
              type="button"
              className={[
                'alty-icon-button',
                scribeSettingsOpen ? 'active' : '',
                brokerRestartNeeded || appUpdateAvailable ? 'restart-needed' : '',
              ].filter(Boolean).join(' ')}
              title={
                brokerRestartNeeded || appUpdateAvailable
                  ? 'Settings — Alt-Y update ready, click Reload Alt-Y to apply'
                  : 'Settings'
              }
              aria-label="Settings"
              aria-pressed={scribeSettingsOpen}
              onClick={() => { setAutoSubmitTip(false); setScribeSettingsOpen(!scribeSettingsOpen); }}
            >
              <i className="fas fa-cog" aria-hidden="true" />
              {brokerRestartNeeded || appUpdateAvailable ? <span className="alty-cog-badge" aria-hidden="true" /> : null}
            </button>
            {autoSubmitTip && !scribeSettingsOpen ? (
              <div className="alty-autosubmit-tip" role="status">
                <span>Auto-submit is on — dictation sends as soon as it finishes. Open Settings to turn it off.</span>
                <button
                  type="button"
                  className="alty-autosubmit-tip-close"
                  aria-label="Dismiss"
                  onClick={() => setAutoSubmitTip(false)}
                >
                  <i className="fas fa-times" aria-hidden="true" />
                </button>
              </div>
            ) : null}
            {scribeSettingsOpen ? (
              <div className="alty-scribe-popover" role="dialog" aria-label="Settings">
                {canExitToBase ? (
                  <button
                    type="button"
                    className="alty-scribe-action"
                    title="Leave Alt-Y and go back to the base site"
                    onClick={() => { window.location.href = exitToBaseUrl; }}
                  >
                    <i className="fas fa-sign-out-alt" aria-hidden="true" />
                    <span>Exit Alt-Y</span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className={(brokerRestartNeeded || appUpdateAvailable) ? 'alty-scribe-action restart-needed' : 'alty-scribe-action'}
                  title="Reload Alt-Y — also restarts the Alt-Y server if back-end changes are pending"
                  disabled={brokerRestarting}
                  onClick={handleReloadAltY}
                >
                  <i className={`fas ${brokerRestarting ? 'fa-spinner fa-spin' : 'fa-sync'}`} aria-hidden="true" />
                  <span>
                    {brokerRestarting
                      ? 'Restarting Alt-Y…'
                      : (brokerRestartNeeded || appUpdateAvailable)
                        ? 'Reload Alt-Y (update ready)'
                        : 'Reload Alt-Y'}
                  </span>
                </button>
                <button
                  type="button"
                  className="alty-scribe-action"
                  title="Promote the Alt-Y-related AI branch changes to testing and report any conflict resolutions"
                  onClick={() => { chat.promoteAiToTesting(); setScribeSettingsOpen(false); }}
                >
                  <i className="fas fa-level-up-alt" aria-hidden="true" />
                  <span>Promote AI to testing</span>
                </button>
                <button
                  type="button"
                  className="alty-scribe-action"
                  title="Promote testing to master, create the live tag, and report any conflict resolutions"
                  onClick={() => { chat.promoteTestingToLive(); setScribeSettingsOpen(false); }}
                >
                  <i className="fas fa-tag" aria-hidden="true" />
                  <span>Promote testing to live</span>
                </button>
                <div className="alty-scribe-divider" />
                {/* Chat actions — shown in the cog menu on both desktop and
                    mobile. Each carries a one-line description so "Deep-improve"
                    in particular isn't an unexplained icon. */}
                {improveAgentLabel && onImproveAgent ? (
                  // Only when the chat is running a user-created agent: reflect
                  // on this chat against the agent's own diagram and rewrite the
                  // agent so the next run goes better (or works at all).
                  <button
                    type="button"
                    className="alty-scribe-action has-desc"
                    title={`Look at “${improveAgentLabel}”’s diagram and this chat, then rewrite the agent to do it better next time`}
                    onClick={() => { onImproveAgent(); setScribeSettingsOpen(false); }}
                  >
                    <i className="fas fa-wrench" aria-hidden="true" />
                    <span className="alty-scribe-action-text">
                      <span className="alty-scribe-action-label">Improve agent</span>
                      <span className="alty-scribe-action-desc">
                        Reflect on this chat against {improveAgentLabel}’s diagram, then rewrite the agent to fix what went wrong or run more efficiently.
                      </span>
                    </span>
                  </button>
                ) : null}
                <button
                  type="button"
                  className="alty-scribe-action has-desc"
                  title="Reflect on this chat and persist reusable fixes into memory & skills"
                  onClick={() => { chat.improveFromChat(); setScribeSettingsOpen(false); }}
                >
                  <i className="fas fa-lightbulb" aria-hidden="true" />
                  <span className="alty-scribe-action-text">
                    <span className="alty-scribe-action-label">Save learnings</span>
                    <span className="alty-scribe-action-desc">Reflect on this chat and save reusable fixes to memory &amp; skills.</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="alty-scribe-action has-desc"
                  title="Build the improvement discussed here on a fresh dev branch and hand back a diff to review"
                  onClick={() => { chat.deepImprove(); setScribeSettingsOpen(false); }}
                >
                  <i className="fas fa-magic" aria-hidden="true" />
                  <span className="alty-scribe-action-text">
                    <span className="alty-scribe-action-label">Deep-improve</span>
                    <span className="alty-scribe-action-desc">Build the change discussed here on a fresh dev branch, then return a diff to review.</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="alty-scribe-action has-desc"
                  title="Copy a condensed, readable version of this chat to the clipboard"
                  onClick={() => { void chat.copyTranscript(); setScribeSettingsOpen(false); }}
                >
                  <i className="fas fa-copy" aria-hidden="true" />
                  <span className="alty-scribe-action-text">
                    <span className="alty-scribe-action-label">Copy chat</span>
                    <span className="alty-scribe-action-desc">Copy a condensed transcript — tool calls are trimmed to the call plus a snippet of output.</span>
                  </span>
                </button>
                <button
                  type="button"
                  className="alty-scribe-action has-desc"
                  title={activeChatId == null ? 'No active chat ID to copy' : `Copy chat ID ${activeChatId} to the clipboard`}
                  disabled={activeChatId == null}
                  onClick={() => {
                    if (activeChatId == null) {
                      chat.addMeta('no active chat id');
                      return;
                    }
                    void copyTextToClipboard(String(activeChatId)).then(
                      () => chat.addMeta(`copied chat id ${activeChatId}`),
                      (error) => chat.addMeta(`copy id failed: ${(error as Error).message || error}`),
                    );
                    setScribeSettingsOpen(false);
                  }}
                >
                  <i className="fas fa-hashtag" aria-hidden="true" />
                  <span className="alty-scribe-action-text">
                    <span className="alty-scribe-action-label">Copy chat ID</span>
                    <span className="alty-scribe-action-desc">
                      Copy this chat&apos;s numeric ID: {activeChatId ?? 'none'}.
                    </span>
                  </span>
                </button>
                <button
                  type="button"
                  className="alty-scribe-action has-desc"
                  title="Clear locally cached chat history from this browser"
                  onClick={() => { chat.clearChatCache(); setScribeSettingsOpen(false); }}
                >
                  <i className="fas fa-eraser" aria-hidden="true" />
                  <span className="alty-scribe-action-text">
                    <span className="alty-scribe-action-label">Clear chat cache</span>
                    <span className="alty-scribe-action-desc">Remove cached chat history from this browser; server history stays untouched.</span>
                  </span>
                </button>
                <div className="alty-scribe-divider" />
                {/* Fast mode lives in Settings so the header only carries the
                    chat title and model picker. */}
                {supportsFastInSettings ? (
                  <label
                    className="alty-scribe-toggle"
                    title={model === 'claude' ? claudeFastModeTooltip() : codexFastModeTooltip()}
                  >
                    <input
                      type="checkbox"
                      checked={fast}
                      onChange={(event) => setFast(event.target.checked)}
                    />
                    <span><i className="fas fa-bolt" aria-hidden="true" /> Fast mode</span>
                  </label>
                ) : null}
                {supportsMax ? (
                  <label className="alty-scribe-toggle" title={cursorMaxModeTooltip()}>
                    <input
                      type="checkbox"
                      checked={maxMode}
                      onChange={(event) => setMaxMode(event.target.checked)}
                    />
                    <span><i className="fas fa-expand" aria-hidden="true" /> Max mode</span>
                  </label>
                ) : null}
                <label className="alty-scribe-toggle">
                  <input
                    type="checkbox"
                    checked={showToolCalls}
                    onChange={(event) => setShowToolCalls(event.target.checked)}
                  />
                  <span>Show tool calls</span>
                </label>
                <label className="alty-scribe-toggle">
                  <input
                    type="checkbox"
                    checked={showActivity}
                    onChange={(event) => setShowActivity(event.target.checked)}
                  />
                  <span>Show activity</span>
                </label>
                <label className="alty-scribe-toggle" title="Replace the transcript with the canvas and make each turn answer on the canvas.">
                  <input
                    type="checkbox"
                    checked={canvasOnly}
                    onChange={(event) => onCanvasOnlyChange?.(event.target.checked)}
                    disabled={!onCanvasOnlyChange}
                  />
                  <span><i className="fas fa-paint-brush" aria-hidden="true" /> Canvas mode</span>
                </label>
                <div className="alty-scribe-divider" />
                <div className="alty-scribe-popover-title">Speech to text</div>
                {!scribeActiveKey.trim() ? (
                  <div className={scribeKeyNudge ? 'alty-scribe-callout nudge' : 'alty-scribe-callout'}>
                    <i className="fas fa-key" aria-hidden="true" />
                    <div>
                      <div>Paste a{scribeIsGroq ? ' Groq' : 'n ElevenLabs'} API key below to dictate with your voice.</div>
                      <a
                        href={scribeIsGroq ? 'https://console.groq.com/keys' : 'https://elevenlabs.io/app/settings/api-keys'}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Get a{scribeIsGroq ? ' Groq' : ' free'} API key →
                      </a>
                    </div>
                  </div>
                ) : null}
                <label className="alty-scribe-field">
                  <span>Model</span>
                  <select
                    value={scribeModel}
                    onChange={(event) => setScribeModel(event.target.value as ScribeModel)}
                  >
                    {(Object.keys(SCRIBE_MODEL_LABELS) as ScribeModel[]).map((id) => (
                      <option key={id} value={id}>{SCRIBE_MODEL_LABELS[id]}</option>
                    ))}
                  </select>
                </label>
                <label className="alty-scribe-field">
                  <span>Language</span>
                  <select
                    value={scribeLanguage}
                    onChange={(event) => setScribeLanguage(event.target.value)}
                  >
                    {Object.keys(SCRIBE_LANGUAGE_LABELS).map((code) => (
                      <option key={code} value={code}>{SCRIBE_LANGUAGE_LABELS[code]}</option>
                    ))}
                  </select>
                </label>
                <label className="alty-scribe-toggle" title="Send your message automatically as soon as dictation finishes transcribing — no need to press Send.">
                  <input
                    type="checkbox"
                    checked={autoSubmit}
                    onChange={(event) => setAutoSubmit(event.target.checked)}
                  />
                  <span>Auto submit</span>
                </label>
                {scribeIsGroq ? (
                  <label className="alty-scribe-field">
                    <span className="alty-scribe-field-label">
                      <span>Groq API key</span>
                      {renderKeySaveBadge(scribeKeySaveStatus)}
                    </span>
                    <input
                      ref={scribeKeyInputRef}
                      type="password"
                      value={scribeGroqApiKey}
                      placeholder="gsk_…"
                      autoComplete="off"
                      spellCheck={false}
                      className={scribeKeyNudge ? 'nudge' : undefined}
                      onChange={(event) => {
                        setScribeGroqApiKey(event.target.value.trim());
                        setScribeKeySaveStatus('saving');
                      }}
                    />
                  </label>
                ) : (
                  <label className="alty-scribe-field">
                    <span className="alty-scribe-field-label">
                      <span>ElevenLabs API key</span>
                      {renderKeySaveBadge(scribeKeySaveStatus)}
                    </span>
                    <input
                      ref={scribeKeyInputRef}
                      type="password"
                      value={scribeApiKey}
                      placeholder="sk_…"
                      autoComplete="off"
                      spellCheck={false}
                      className={scribeKeyNudge ? 'nudge' : undefined}
                      onChange={(event) => {
                        setScribeApiKey(event.target.value.trim());
                        setScribeKeySaveStatus('saving');
                      }}
                    />
                  </label>
                )}
                <p className="alty-scribe-hint">
                  {scribeIsGroq
                    ? 'Groq Whisper transcribes after you stop.'
                    : 'Realtime streams as you speak; Scribe v2 transcribes after you stop.'}{' '}
                  {scribeOwner
                    ? <>Saved on the server to <strong>{scribeOwner}</strong>.</>
                    : 'Saved to your SuperAdmin account.'}
                </p>
                <div className="alty-scribe-divider" />
                <div className="alty-scribe-popover-title">API keys</div>
                <p className="alty-scribe-hint">
                  Bring-your-own-key for the cross-model MCP tools. Agents in
                  any chat can call <code>openrouter_chat</code> or
                  <code> openai_chat</code> to consult another model. Without a
                  key the tool tells the user to add one here.
                </p>
                <label className="alty-scribe-field">
                  <span className="alty-scribe-field-label">
                    <span>OpenRouter API key</span>
                    {renderKeySaveBadge(openrouterKeySaveStatus)}
                  </span>
                  <input
                    type="password"
                    value={openrouterApiKey}
                    placeholder="sk-or-v1-…"
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(event) => {
                      setOpenrouterApiKey(event.target.value.trim());
                      setOpenrouterKeySaveStatus('saving');
                    }}
                  />
                </label>
                <p className="alty-scribe-hint">
                  <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer">
                    Get an OpenRouter key →
                  </a>
                </p>
                <label className="alty-scribe-field">
                  <span className="alty-scribe-field-label">
                    <span>OpenAI API key</span>
                    {renderKeySaveBadge(openaiKeySaveStatus)}
                  </span>
                  <input
                    type="password"
                    value={openaiApiKey}
                    placeholder="sk-…"
                    autoComplete="off"
                    spellCheck={false}
                    onChange={(event) => {
                      setOpenaiApiKey(event.target.value.trim());
                      setOpenaiKeySaveStatus('saving');
                    }}
                  />
                </label>
                <p className="alty-scribe-hint">
                  <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer">
                    Get an OpenAI key →
                  </a>
                </p>
              </div>
            ) : null}
          </div>
        </header>
        <div ref={messagesRef} className={canvasOnly ? 'alty-messages alty-messages-canvas-only' : 'alty-messages'}>
          {canvasOnly ? (
            <div className="alty-canvas-only-panel">
              <WorkspaceCanvas
                content={canvasContent}
                badge={canvasBadge}
                multiEnv={false}
              />
            </div>
          ) : (
            <>
              {(() => {
                const visible = showToolCalls ? chat.messages : chat.messages.filter((m) => m.role !== 'tool');
                return visible.length ? visible.map((message) => (
                  <Message
                    key={message.id}
                    message={message}
                    onRerun={handleRerunMessage}
                    onExpandToolOutput={handleExpandToolOutput}
                    canvasModeActive={!activeTabId || canvasOnly}
                  />
                )) : (
                  <div className="alty-empty">Open or start a chat.</div>
                );
              })()}
              {showActivity && chat.activities.length ? (
                <ActivityPanel activities={chat.activities} busy={chat.busy} />
              ) : null}
              {chat.thinking ? (
                <ThinkingFooter
                  startedAt={chat.thinking.startedAt}
                  outputTokens={chat.thinking.outputTokens}
                  activeTool={chat.thinking.activeTool}
                />
              ) : null}
              {chat.queuedMessages.map((queued) => (
                // A message the user sent while a turn was still running. Shown as
                // a dimmed pending bubble (they auto-send in order as each turn
                // ends); the ✕ cancels that one before it goes out.
                <div key={queued.id} className="alty-message user queued">
                  <div className="alty-message-bubble">
                    <div className="alty-queued-head">
                      <span className="alty-queued-tag">
                        <i className="fas fa-clock" aria-hidden="true" /> Queued
                      </span>
                      <button
                        type="button"
                        className="alty-queued-cancel"
                        title="Cancel queued message"
                        aria-label="Cancel queued message"
                        onClick={() => chat.cancelQueued(queued.id)}
                      >
                        <i className="fas fa-times" aria-hidden="true" />
                      </button>
                    </div>
                    {queued.images?.length ? (
                      <div className="alty-message-images">
                        {queued.images.map((attachment, index) => (
                          attachment.mediaType.startsWith('image/') ? (
                            <img
                              key={index}
                              src={`data:${attachment.mediaType};base64,${attachment.dataBase64}`}
                              alt={attachment.name || 'attached image'}
                            />
                          ) : (
                            <span key={index} className="alty-message-file" title={attachment.name}>
                              <i className="fas fa-file" aria-hidden="true" />
                              <span>{attachment.name || 'file'}</span>
                            </span>
                          )
                        ))}
                      </div>
                    ) : null}
                    {queued.text ? <MarkdownText text={queued.text} /> : null}
                  </div>
                </div>
              ))}
              {showDoneSummary ? (
                <div className="alty-done-summary" role="note">
                  <div className="alty-done-summary-body">
                    {activeLastTurnSummary ? (
                      <div className="alty-done-summary-line">
                        <span className="alty-done-summary-label">Last turn</span>
                        <span className="alty-done-summary-text">{activeLastTurnSummary}</span>
                      </div>
                    ) : null}
                    {activeOverallSummary ? (
                      <div className="alty-done-summary-line">
                        <span className="alty-done-summary-label">Overall</span>
                        <span className="alty-done-summary-text">{activeOverallSummary}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </>
          )}
        </div>
        <footer
          className="alty-composer"
          onDrop={handleDrop}
          onDragOver={(event) => {
            if (Array.from(event.dataTransfer.types).includes('Files')) event.preventDefault();
          }}
        >
          <div className="alty-composer-main">
            {/* Next-turn cost chip — top-right above the textarea. Always
                rendered (even on an empty chat); the chip itself returns
                an invisible span until it has data so layout stays stable. */}
            <div className="alty-prefix-cost-row">
              <PrefixCostChip
                chatId={chat.activeRunId}
                model={model}
                busy={chat.busy}
                wsEstimate={chat.prefixEstimate}
              />
            </div>
            {pendingImages.length ? (
              <div className="alty-attachments">
                {pendingImages.map((attachment, index) => {
                  const isImg = attachment.mediaType.startsWith('image/');
                  return (
                    <div className={isImg ? 'alty-attachment' : 'alty-attachment file'} key={index}>
                      {isImg ? (
                        <img src={`data:${attachment.mediaType};base64,${attachment.dataBase64}`} alt={attachment.name} />
                      ) : (
                        <div className="alty-attachment-file" title={attachment.name}>
                          <i className="fas fa-file" aria-hidden="true" />
                          <span className="alty-attachment-name">{attachment.name}</span>
                        </div>
                      )}
                      <button
                        type="button"
                        className="alty-attachment-remove"
                        title="Remove attachment"
                        onClick={() => setPendingImages((list) => list.filter((_, i) => i !== index))}
                      >
                        <i className="fas fa-times" aria-hidden="true" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}
            <div className="alty-input-wrap">
              <textarea
                ref={textareaRef}
                value={draft}
                placeholder={(() => {
                  if (userIds.length === 0) return "Message Alt-Y — attach files, images or screenshots";
                  if (userIds.length === 1) {
                    const id = userIds[0];
                    const label = chat.participants?.find((p) => p.id === id)?.label || id;
                    return `Message ${label} — user to user`;
                  }
                  return `Message ${userIds.length} users — user to user`;
                })()}
                rows={2}
                onChange={(event) => setDraft(event.target.value)}
                onPaste={handlePaste}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    send();
                  }
                }}
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="alty-file-input"
                onChange={handleAttachInput}
              />
              {/* Actions row — a flex toolbar inside the input box, beneath the
                  textarea, on both desktop and mobile. The user types in a clean
                  full-width field above the clip / mic / send icons. */}
              <div className="alty-composer-actions">
              <button
                type="button"
                className="alty-attach-button"
                title="Attach files, images or screenshots"
                aria-label="Attach files, images or screenshots"
                onClick={() => fileInputRef.current?.click()}
              >
                <i className="fas fa-paperclip" aria-hidden="true" />
              </button>
              <button
                type="button"
                className={scribe.recording || scribe.starting ? 'alty-mic-button recording' : 'alty-mic-button'}
                data-tooltip={isMobile ? undefined : scribe.recording || scribe.starting ? 'Stop transcribing' : 'Hold Right Shift to talk'}
                aria-label={scribe.recording || scribe.starting ? 'Stop transcribing' : 'Transcribe speech — hold Right Shift to talk'}
                aria-pressed={scribe.recording || scribe.starting}
                disabled={scribe.busy}
                onClick={handleMicToggle}
              >
                <i
                  className={`fas ${scribe.busy ? 'fa-spinner fa-spin' : scribe.recording || scribe.starting ? 'fa-stop' : 'fa-microphone'}`}
                  aria-hidden="true"
                />
              </button>
              {chat.busy ? (
                <>
                  <button
                    type="button"
                    className="alty-queue-button"
                    title="Queue message (Enter)"
                    aria-label="Queue message"
                    onClick={send}
                    disabled={!draft.trim() && pendingImages.length === 0}
                  >
                    <i className="fas fa-clock" aria-hidden="true" />
                    <span>Queue</span>
                  </button>
                  <button type="button" className="alty-stop-button" title="Stop" aria-label="Stop" onClick={chat.stop}>
                    <i className="fas fa-stop" aria-hidden="true" />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="alty-send-button"
                  title="Send (Enter)"
                  aria-label="Send (Enter)"
                  onClick={send}
                  disabled={!draft.trim() && pendingImages.length === 0}
                >
                  <i className="fas fa-arrow-up" aria-hidden="true" />
                </button>
              )}
              </div>
            </div>
            {scribe.error ? (
              <div className="alty-scribe-status error">
                <i className="fas fa-exclamation-circle" aria-hidden="true" /> {scribe.error}
              </div>
            ) : scribe.busy ? (
              <div className="alty-scribe-status">
                <i className="fas fa-spinner fa-spin" aria-hidden="true" /> Transcribing…
              </div>
            ) : scribe.recording || scribe.starting ? (
              <div className="alty-scribe-status recording">
                <span className="alty-scribe-dot" aria-hidden="true" />
                {scribe.starting
                  ? <span>Starting…</span>
                  : scribeModel === 'scribe_v2_realtime'
                    ? <span>Listening… {scribe.partial}</span>
                    : <span>Recording — tap the mic to transcribe</span>}
              </div>
            ) : scribeKeyNudge && !scribeActiveKey.trim() ? (
              // Inline hint right by the mic the user just tapped — the cog
              // popover (top-right) is easy to miss, especially on mobile.
              <div className="alty-scribe-status needs-key">
                <i className="fas fa-key" aria-hidden="true" />
                <span>Add a{scribeIsGroq ? ' Groq' : 'n ElevenLabs'} API key in <button type="button" className="alty-scribe-link" onClick={() => setScribeSettingsOpen(true)}>Settings ⚙</button> to dictate.</span>
                <a href={scribeIsGroq ? 'https://console.groq.com/keys' : 'https://elevenlabs.io/app/settings/api-keys'} target="_blank" rel="noopener noreferrer">Get a{scribeIsGroq ? ' Groq' : ' free'} key →</a>
              </div>
            ) : null}
          </div>
        </footer>
      </section>
      {showParticipants && activeChat && (
        <ParticipantsPopover
          participants={chat.participants || []}
          pinnedAgents={pinnedAgents}
          onAddParticipant={chat.addParticipant}
          onClose={() => setShowParticipants(false)}
        />
      )}
    </aside>
  );
}
